import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { handleTokenAndPostRequest } from "../utils/utils";
import { FaSave, FaUndo, FaUpload, FaInfoCircle, FaTimes, FaExclamationTriangle, FaLock, FaSignOutAlt, FaMapMarkerAlt, FaFacebook, FaPlug, FaTrash, FaPlus, FaBell, FaRobot, FaEnvelopeOpenText, FaTag, FaPen, FaCheck, FaExternalLinkAlt, FaUser, FaEnvelope, FaMobileAlt, FaThList, FaStream, FaListUl } from "react-icons/fa";
import ConnectCRMModal from "../components/ConnectCRMModal";
import UploadMboxModal from "../components/UploadMboxModal";
import BuyExtraUsageModal from "../components/BuyExtraUsageModal";
import CampaignSmsTemplatesEditor from "../components/CampaignSmsTemplatesEditor";
import { ContactTagChip, clearTagDefinitionsCache } from "../components/ContactTagPicker";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const SPINNER_KEYFRAMES = `@keyframes settingsSpin { to { transform: rotate(360deg); } }`;

function InlineSpinner({ size = 14, color = "#fff" }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size,
      border: `2px solid rgba(255,255,255,0.3)`, borderTopColor: color,
      borderRadius: "50%", animation: "settingsSpin 0.7s linear infinite",
    }} />
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobile;
}

const FIELDS = [
  {
    key: "agent_name",
    label: "Full Name",
    type: "text",
    hint: "Displayed on your property reports, email notifications, landing page, and all client-facing communications.",
  },
  {
    key: "agent_first_name",
    label: "First Name",
    type: "text",
    hint: "Used in personalised greetings in emails and AI-generated content such as articles and task manager responses.",
  },
  {
    key: "agent_email",
    label: "Email Address",
    type: "email",
    hint: "Your primary login email. Also used as the reply-to address on outbound emails and notifications.",
  },
  {
    key: "agent_notification_email",
    label: "Notification Email",
    type: "email",
    hint: "CC'd on all lead notifications and platform alerts you receive. Useful if you want a copy going to a separate inbox or assistant.",
  },
  {
    key: "mobile",
    label: "Mobile Number",
    type: "tel",
    hint: "Displayed on your published appraisals so prospects can call or text you with one tap. Use international format, e.g. +61421346175.",
  },
  {
    key: "agent_title",
    label: "Job Title",
    type: "text",
    hint: "Shown beneath your name on your landing page and property report emails (e.g. 'Licensed Real Estate Agent').",
  },
  {
    key: "agent_description",
    label: "Bio / Description",
    type: "textarea",
    hint: "A short bio shown on your landing page and included in property report emails. Helps personalise your brand for prospective vendors.",
  },
  {
    key: "agency_name_long",
    label: "Agency Name (Full)",
    type: "text",
    hint: "The full legal or display name of your agency. Shown on property reports, AI email replies, and your public landing page.",
  },
  {
    key: "agency_name_short",
    label: "Agency Name (Short)",
    type: "text",
    hint: "A shorter version of your agency name used in compact spaces like email subject lines and notification badges.",
  },
  {
    key: "agent_website",
    label: "Website",
    type: "url",
    hint: "Your agency or personal website URL. Linked from your landing page so prospective vendors can learn more about you.",
  },
  {
    key: "agent_contact_link",
    label: "Contact Link",
    type: "url",
    hint: "The URL visitors are directed to when they click 'Contact' or enquiry buttons on your landing page. Can be your website contact page, a calendar booking link, etc.",
  },
  {
    key: "agency_facebook_link",
    label: "Facebook Page URL",
    type: "url",
    hint: "Linked from your landing page and email footers. Helps build your social media presence with prospective vendors.",
  },
  {
    key: "agency_instagram_link",
    label: "Instagram Profile URL",
    type: "url",
    hint: "Linked from your landing page and email footers alongside your Facebook page.",
  },
  {
    key: "logo_url",
    label: "Agency Logo",
    type: "image",
    hint: "Your agency logo displayed on your landing page header, property reports, and email templates.",
  },
  {
    key: "agent_profile_image",
    label: "Profile Photo",
    type: "image",
    hint: "Your headshot displayed on your landing page, property report emails, and market update newsletters.",
  },
  {
    key: "agent_colour",
    label: "Brand Colour",
    type: "color",
    hint: "Your primary brand colour used for buttons, headers, and accents across your landing page and all email templates.",
  },
  {
    key: "agent_font_colour",
    label: "Font Colour",
    type: "color",
    hint: "The text colour used over your brand colour on buttons and headers. Typically white (#ffffff) or dark (#333333) depending on your brand colour.",
  },
  {
    key: "agent_button_colour",
    label: "Button Colour",
    type: "color",
    hint: "The colour for call-to-action buttons (e.g. 'Get Appraisal', 'View Report') on your landing page and emails. Can differ from your main brand colour for contrast.",
  },
  {
    key: "agent_font_url",
    label: "Custom Font",
    type: "font",
    hint: "Upload your brand typography (OTF, TTF, WOFF, or WOFF2). The file is stored against your agent profile so it can be applied to your branded surfaces.",
  },
  {
    key: "default_market_update_state",
    label: "Default Market Update State",
    type: "text",
    hint: "The default Australian state (e.g. 'NSW', 'VIC') pre-selected when generating market update newsletters for your area.",
  },
  {
    key: "default_market_update_postcode",
    label: "Default Market Update Postcode",
    type: "text",
    hint: "The default postcode pre-filled when creating market update reports, so you don't have to enter it each time.",
  },
  {
    key: "default_market_update_suburb",
    label: "Default Market Update Suburb",
    type: "text",
    hint: "The default suburb pre-filled for market update newsletters, matching your primary area of operation.",
  },
  {
    key: "global_context",
    label: "Valuation Global Context",
    type: "textarea",
    hint: "Hyperlocal insights included in every AI valuation and property analysis. Use this for knowledge like 'Houses on X street typically trade at a 20% premium' or 'Properties backing onto the reserve command higher prices'. This context influences all property reports, comparable analyses, and update emails.",
  },
];

const LOCKED_FIELDS = new Set(["agent_email"]);

export default function Settings() {
  const isMobile = useIsMobile();
  const [agentDetails, setAgentDetails] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [originalValues, setOriginalValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [savingSection, setSavingSection] = useState(null);
  const [sectionSaveMsgs, setSectionSaveMsgs] = useState({});
  const [uploadingField, setUploadingField] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const navigate = useNavigate();

  // Notification settings state
  const NOTIFICATION_EVENT_TYPES = [
    { key: "property_report_request", label: "Property Report Request", description: "When a homeowner requests a property report on your page." },
    { key: "property_analysis", label: "Property Analysis View", description: "When a homeowner views a property analysis using the DIY valuator." },
    { key: "suburb_analysis", label: "Suburb Analysis View", description: "When a homeowner views a suburb analysis on your page." },
    { key: "suburb_profile_view", label: "Suburb Profile Visit", description: "When a homeowner visits a suburb profile page on your branded site." },
    { key: "start_enquiry_form", label: "Enquiry Form Opened", description: "When a user opens the enquiry form on your landing page." },
    { key: "start_request_appraisal_review", label: "Appraisal Review Started", description: "When a homeowner starts an appraisal review request." },
    { key: "submit_request_appraisal_review", label: "Appraisal Review Submitted", description: "When a homeowner submits an appraisal review request." },
    { key: "request_sign_in_code", label: "Sign In Started", description: "When a user begins the sign-in process on your page." },
    { key: "onboarding_complete", label: "New Analysis Started", description: "When a user starts a property analysis on your page." },
    { key: "share_valuation", label: "Valuation Shared", description: "When a user shares a property valuation with someone else." },
    { key: "track_property", label: "Property Tracked", description: "When a user adds a property to their tracked watchlist." },
    { key: "save_notification_settings", label: "Notification Preferences Saved", description: "When a user saves their notification preferences (tracked properties / suburbs) on your page." },
    { key: "property_analysis_complete", label: "Property Analysis Completed", description: "When an AI property analysis is completed." },
    { key: "property_report_sent", label: "Property Report Sent", description: "When a property report has been generated and sent." },
    { key: "property_report_processing", label: "Property Report Processing", description: "When a property report is being generated." },
    { key: "property_sms_link_sent", label: "SMS Report Link Sent", description: "When a property report link is sent via SMS." },
  ];
  const [notifEnabled, setNotifEnabled] = useState(true);
  const [notifEventTypes, setNotifEventTypes] = useState(() => {
    const defaults = {};
    NOTIFICATION_EVENT_TYPES.forEach(e => { defaults[e.key] = true; });
    return defaults;
  });
  const [originalNotifEnabled, setOriginalNotifEnabled] = useState(true);
  const [originalNotifEventTypes, setOriginalNotifEventTypes] = useState(() => {
    const defaults = {};
    NOTIFICATION_EVENT_TYPES.forEach(e => { defaults[e.key] = true; });
    return defaults;
  });
  const [savingNotif, setSavingNotif] = useState(false);
  const [notifSaveMsg, setNotifSaveMsg] = useState("");

  // Outbound campaign job toggles (stored under activated_features as
  // { <key>: { enabled: bool } } to match the existing activated_features
  // shape e.g. sms_property_updates). Each key matches the corresponding
  // backend job module name. Defaults to enabled (true) so existing agents
  // who haven't visited this section keep their current behavior. Backend
  // jobs read these via (activated_features->'{key}'->>'enabled')::boolean
  // with COALESCE(..., true).
  const JOB_CAMPAIGNS = [
    {
      key: "price_update_email",
      label: "Suburb Update Emails",
      description: "Market update emails sent to your contacts when their suburb's median price changes.",
    },
    {
      key: "new_data_available_sms",
      label: "Suburb Update SMS",
      description: "SMS sent to mobile-only contacts when new property data is available, linking them to your branded valuation page.",
      isSms: true,
    },
    {
      key: "property_update_email",
      label: "Property Update Emails",
      description: "Property report emails sent to your contacts with the latest estimate for the property they're tracking.",
    },
    {
      key: "property_update_sms",
      label: "Property Update SMS",
      description: "SMS sent alongside the property update email (where a mobile is available), linking contacts to their latest estimate.",
      isSms: true,
    },
  ];
  const SMS_DAILY_LIMIT_DEFAULT = 5;
  const [jobSettings, setJobSettings] = useState(() => {
    const defaults = {};
    JOB_CAMPAIGNS.forEach(j => { defaults[j.key] = true; });
    return defaults;
  });
  const [originalJobSettings, setOriginalJobSettings] = useState(() => {
    const defaults = {};
    JOB_CAMPAIGNS.forEach(j => { defaults[j.key] = true; });
    return defaults;
  });
  // Per-SMS-campaign daily send limit (per agent).
  const [jobDailyLimits, setJobDailyLimits] = useState(() => {
    const defaults = {};
    JOB_CAMPAIGNS.filter(j => j.isSms).forEach(j => { defaults[j.key] = SMS_DAILY_LIMIT_DEFAULT; });
    return defaults;
  });
  const [originalJobDailyLimits, setOriginalJobDailyLimits] = useState(() => {
    const defaults = {};
    JOB_CAMPAIGNS.filter(j => j.isSms).forEach(j => { defaults[j.key] = SMS_DAILY_LIMIT_DEFAULT; });
    return defaults;
  });
  // Account credit balance (cents), read-only here. This is the unified
  // balance (production.agent_balances) — topped up by admins or via /billing.
  const [balanceCents, setBalanceCents] = useState(0);
  const [showBuyCredit, setShowBuyCredit] = useState(false);
  const [savingJobs, setSavingJobs] = useState(false);
  const [jobsSaveMsg, setJobsSaveMsg] = useState("");
  // Parts count of the follow-up SMS template per campaign, reported up by
  // CampaignSmsTemplatesEditor so we can show an accurate max daily cost.
  const [smsFollowUpParts, setSmsFollowUpParts] = useState({});

  // Send-sample state for the property/market update campaign emails. The
  // address fields default to the logged-in agent's email (set on load).
  const [samplePropertyEmail, setSamplePropertyEmail] = useState("");
  const [samplePropertySending, setSamplePropertySending] = useState(false);
  const [samplePropertyMsg, setSamplePropertyMsg] = useState("");
  const [sampleMarketEmail, setSampleMarketEmail] = useState("");
  const [sampleMarketSending, setSampleMarketSending] = useState(false);
  const [sampleMarketMsg, setSampleMarketMsg] = useState("");

  // Send-sample state for the property/market update campaign SMS. The mobile
  // fields default to the logged-in agent's stored number (set on load).
  const [samplePropertySms, setSamplePropertySms] = useState("");
  const [samplePropertySmsSending, setSamplePropertySmsSending] = useState(false);
  const [samplePropertySmsMsg, setSamplePropertySmsMsg] = useState("");
  const [sampleMarketSms, setSampleMarketSms] = useState("");
  const [sampleMarketSmsSending, setSampleMarketSmsSending] = useState(false);
  const [sampleMarketSmsMsg, setSampleMarketSmsMsg] = useState("");

  // Editable mobile number shown at the top of the Campaigns section. Persists
  // to agent_details.mobile via /update-agent-details.
  const [campaignMobile, setCampaignMobile] = useState("");
  const [originalCampaignMobile, setOriginalCampaignMobile] = useState("");
  const [savingMobile, setSavingMobile] = useState(false);
  const [mobileSaveMsg, setMobileSaveMsg] = useState("");

  // AI Agent config state (stored under agent_settings.ai_agent_config + dedicated cols)
  const [aiAgentName, setAiAgentName] = useState("");
  const [aiAgentFirstName, setAiAgentFirstName] = useState("");
  const [aiAgentEmailPrefix, setAiAgentEmailPrefix] = useState("");
  const [aiAgentDomain, setAiAgentDomain] = useState("homeprice.site");
  const [aiAgentPhoto, setAiAgentPhoto] = useState("");
  const [aiTitle, setAiTitle] = useState("");
  const [aiAutoReply, setAiAutoReply] = useState(false);
  // Master switch for outbound AI follow-up + warm-up email/SMS campaigns.
  // Three states:
  //   'off'    — no outbound sends or queueing
  //   'on'     — AI sends directly on the agent's behalf
  //   'review' — AI drafts surface in AI Conversations → Needs Review for the
  //              agent to approve, edit, or dismiss before sending.
  // Defaults to 'off' — agents must explicitly opt in.
  const [aiOutboundMode, setAiOutboundMode] = useState('off');
  const [aiGlobalInstructions, setAiGlobalInstructions] = useState("");
  const [aiEmailSignature, setAiEmailSignature] = useState("");
  const [aiSignatureLogo, setAiSignatureLogo] = useState("");
  const [originalAiAgent, setOriginalAiAgent] = useState({
    name: "", first_name: "", email_prefix: "", title: "",
    auto_reply: false, outbound_mode: 'off',
    global_instructions: "", email_signature: "", signature_logo: "",
  });
  const [savingAiAgent, setSavingAiAgent] = useState(false);
  const [aiAgentSaveMsg, setAiAgentSaveMsg] = useState("");
  const [showMboxModal, setShowMboxModal] = useState(false);
  const [uploadingAiPhoto, setUploadingAiPhoto] = useState(false);
  const aiPhotoInputRef = useRef(null);
  const [uploadingSignatureLogo, setUploadingSignatureLogo] = useState(false);
  const signatureLogoInputRef = useRef(null);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testEmailEdited, setTestEmailEdited] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailMsg, setTestEmailMsg] = useState("");
  // Server-provided canonical default system prompt — fetched once so
  // the "Restore Default" button on the System Prompt field can drop
  // the standard template in. Stored verbatim with placeholders.
  const [defaultSystemPrompt, setDefaultSystemPrompt] = useState("");
  const [loadingDefaultPrompt, setLoadingDefaultPrompt] = useState(false);

  // Billing state — used to gate the outbound campaign toggles behind an
  // active (non-trialing) Growth subscription. Mirrors the logic in Billing.js
  // (plan === 'growth' && subscription_status === 'active').
  const [billingStatus, setBillingStatus] = useState(null);
  const isSubscriptionActive = billingStatus?.plan === "growth"
    && billingStatus?.subscription_status === "active";
  const [showBillingRequiredModal, setShowBillingRequiredModal] = useState(false);
  const [showSmsCreditRequiredModal, setShowSmsCreditRequiredModal] = useState(false);

  // Facebook connection state
  const [fbConnected, setFbConnected] = useState(false);
  const [fbPageNames, setFbPageNames] = useState([]);
  const [fbLoading, setFbLoading] = useState(false);
  const [fbMsg, setFbMsg] = useState("");
  const fbPopupRef = useRef(null);

  // Target suburbs multi-select state
  const [targetSuburbs, setTargetSuburbs] = useState([]);       // [{value, label, state}]
  const [originalTargetSuburbIds, setOriginalTargetSuburbIds] = useState([]);
  const [suburbQuery, setSuburbQuery] = useState("");
  const [suburbResults, setSuburbResults] = useState([]);
  const [suburbSearching, setSuburbSearching] = useState(false);
  const [suburbDropdownOpen, setSuburbDropdownOpen] = useState(false);
  const [savingSuburbs, setSavingSuburbs] = useState(false);
  const [suburbSaveMsg, setSuburbSaveMsg] = useState("");
  const suburbSearchTimer = useRef(null);
  const suburbInputRef = useRef(null);
  const suburbDropdownRef = useRef(null);

  // Platform Config — behaviour of the public-facing /property-search landing
  // page. Stored under agent_settings.property_search_config and read by the
  // consumer app's PropertySearch page via the /agency endpoint.
  //
  // Schema:
  //   {
  //     layout: 'single' | 'stepped',
  //     fields: [{ key, enabled, required }, ...]   // keys: name | email | mobile
  //   }
  // Address is always shown + required so it isn't configurable here.
  const PS_FIELD_DEFS = [
    { key: "name",   label: "Name",          icon: <FaUser size={12} /> },
    { key: "email",  label: "Email address", icon: <FaEnvelope size={12} /> },
    { key: "mobile", label: "Mobile number", icon: <FaMobileAlt size={12} /> },
  ];
  const PS_LAYOUTS = [
    {
      key: "single",
      label: "Single form",
      description: "All enabled fields shown together on one screen. This is the default.",
      icon: <FaThList size={13} />,
    },
    {
      key: "stepped",
      label: "Stepped (one at a time)",
      description: "Visitor moves through enabled fields step by step. Good when you're asking for several details.",
      icon: <FaStream size={13} />,
    },
  ];
  const defaultPsFields = () => ({
    name:   { enabled: false, required: false },
    email:  { enabled: true,  required: true  },
    mobile: { enabled: false, required: false },
  });
  const [psLayout, setPsLayout] = useState("single");
  const [originalPsLayout, setOriginalPsLayout] = useState("single");
  const [psFields, setPsFields] = useState(defaultPsFields);
  const [originalPsFields, setOriginalPsFields] = useState(defaultPsFields);
  const [savingPlatformConfig, setSavingPlatformConfig] = useState(false);
  const [platformConfigMsg, setPlatformConfigMsg] = useState("");

  // Property Report sections — which sections show on the live Property Report
  // (the consumer app's /property-profile page). Stored under
  // agent_settings.property_report_config and read by that page via /agency.
  // Days to Sell / Days to Rent default off (historically hidden); the rest on.
  const PR_SECTION_DEFS = [
    { key: "sale-estimate",    label: "Sale Estimate",  description: "AI estimated sale price with low/mid/high range." },
    { key: "sale-comparables", label: "Similar Sold",   description: "Comparable recently sold properties." },
    { key: "sale-days",        label: "Days to Sell",   description: "Estimated days on market to sell." },
    { key: "rent-estimate",    label: "Rent Estimate",  description: "AI estimated weekly rent with low/mid/high range." },
    { key: "rent-comparables", label: "Similar Rented", description: "Comparable recently rented properties." },
    { key: "rent-days",        label: "Days to Rent",   description: "Estimated days on market to rent." },
    { key: "location",         label: "Location",       description: "Amenities, value factors and location insights." },
    { key: "history",          label: "History",        description: "Past sale and listing history timeline." },
  ];
  const defaultReportSections = () => ({
    "sale-estimate": true,
    "sale-comparables": true,
    "sale-days": false,
    "rent-estimate": true,
    "rent-comparables": true,
    "rent-days": false,
    "location": true,
    "history": true,
  });
  const [reportSections, setReportSections] = useState(defaultReportSections);
  const [originalReportSections, setOriginalReportSections] = useState(defaultReportSections);

  // Scrollable tab menu state
  const SECTIONS = [
    { key: "profile", label: "Profile" },
    { key: "agency", label: "Agency" },
    { key: "branding", label: "Branding" },
    { key: "platform_config", label: "Platform Config" },
    { key: "market", label: "Market Defaults" },
    { key: "ai", label: "AI & Valuation" },
    { key: "ai_agent", label: "AI Agent" },
    { key: "suburbs", label: "Suburbs" },
    { key: "integrations", label: "Integrations" },
    { key: "notifications", label: "Notifications" },
    { key: "campaigns", label: "Campaigns" },
    { key: "tags", label: "Contact Tags" },
    ...(agentDetails?.active ? [{ key: "referral", label: "Referral" }] : []),
    { key: "security", label: "Security" },
  ];
  const [activeSection, setActiveSection] = useState("profile");
  const activeSectionRef = useRef("profile");
  const isScrollingRef = useRef(false);
  const sectionRefs = useRef({});
  const tabsRowRef = useRef(null);
  const tabButtonRefs = useRef({});
  const scrollContainerRef = useRef(null);

  // Edge-fade affordances: show a gradient on whichever side has more tabs
  // hidden off-screen, so it's obvious the tab row scrolls horizontally.
  const [tabOverflow, setTabOverflow] = useState({ left: false, right: false });
  const updateTabOverflow = useCallback(() => {
    const el = tabsRowRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setTabOverflow({
      left: el.scrollLeft > 1,
      right: el.scrollLeft < maxScroll - 1,
    });
  }, []);
  useEffect(() => {
    const el = tabsRowRef.current;
    if (!el) return;
    updateTabOverflow();
    el.addEventListener("scroll", updateTabOverflow, { passive: true });
    window.addEventListener("resize", updateTabOverflow);
    return () => {
      el.removeEventListener("scroll", updateTabOverflow);
      window.removeEventListener("resize", updateTabOverflow);
    };
  }, [updateTabOverflow, SECTIONS.length]);

  // Scroll-based section tracking
  const scrollToSection = useCallback((key, behavior = "smooth") => {
    const el = sectionRefs.current[key];
    const scrollContainer = scrollContainerRef.current;
    if (!el || !scrollContainer) return false;
    const containerTop = scrollContainer.getBoundingClientRect().top;
    const elTop = el.getBoundingClientRect().top;
    const offset = scrollContainer.scrollTop + (elTop - containerTop) - 60;
    // Clamp to scrollable range so iOS doesn't ignore an out-of-range value.
    const max = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
    const target = Math.max(0, Math.min(offset, max));
    try {
      scrollContainer.scrollTo({ top: target, behavior });
    } catch {
      scrollContainer.scrollTop = target;
    }
    return true;
  }, []);

  const handleTabClick = useCallback((key) => {
    activeSectionRef.current = key;
    setActiveSection(key);
    isScrollingRef.current = true;
    scrollToSection(key, "smooth");
    setTimeout(() => { isScrollingRef.current = false; }, 800);
  }, [scrollToSection]);

  // Auto-highlight on scroll
  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl) return;
    let ticking = false;
    const onScroll = () => {
      if (isScrollingRef.current) return;
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const keys = SECTIONS.map(s => s.key);
        const containerTop = scrollEl.getBoundingClientRect().top;
        let active = keys[0];
        for (const key of keys) {
          const el = sectionRefs.current[key];
          if (el) {
            const top = el.getBoundingClientRect().top - containerTop;
            if (top <= 80) active = key;
          }
        }
        if (active !== activeSectionRef.current) {
          activeSectionRef.current = active;
          setActiveSection(active);
        }
        ticking = false;
      });
    };
    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    return () => scrollEl.removeEventListener("scroll", onScroll);
  }, [loading]);

  // Auto-scroll active tab into horizontal view as the user scrolls vertically
  // through sections. Uses the tab row's own scrollLeft so it doesn't disturb
  // the page's vertical scroll position (scrollIntoView with block:"nearest"
  // can still nudge ancestor scroll containers on some browsers).
  useEffect(() => {
    const rowEl = tabsRowRef.current;
    const tabEl = tabButtonRefs.current[activeSection];
    if (!rowEl || !tabEl) return;
    const rowRect = rowEl.getBoundingClientRect();
    const tabRect = tabEl.getBoundingClientRect();
    const padding = 8;
    if (tabRect.left < rowRect.left + padding || tabRect.right > rowRect.right - padding) {
      const tabCenter = tabEl.offsetLeft + tabEl.offsetWidth / 2;
      const target = tabCenter - rowEl.clientWidth / 2;
      const max = Math.max(0, rowEl.scrollWidth - rowEl.clientWidth);
      const left = Math.max(0, Math.min(target, max));
      try {
        rowEl.scrollTo({ left, behavior: "smooth" });
      } catch {
        rowEl.scrollLeft = left;
      }
    }
  }, [activeSection]);

  // Honor URL hash deep-link (e.g. /settings#ai_agent) once content has rendered.
  useEffect(() => {
    if (loading) return;
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash) return;
    const validKeys = SECTIONS.map((s) => s.key);
    if (!validKeys.includes(hash)) return;

    activeSectionRef.current = hash;
    setActiveSection(hash);
    isScrollingRef.current = true;

    // Retry the scroll several times as async content (images, integrations,
    // conversation flows) loads in below — section offsets shift over time.
    // Use instant behavior on iOS Safari where smooth scroll inside a nested
    // container is unreliable; fall back to a final smooth pass.
    let cancelled = false;
    const attempts = [0, 80, 200, 500, 1000, 1800];
    const timers = attempts.map((delay, i) =>
      setTimeout(() => {
        if (cancelled) return;
        const isLast = i === attempts.length - 1;
        scrollToSection(hash, "auto");
        if (isLast) {
          isScrollingRef.current = false;
        }
      }, delay)
    );

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      isScrollingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, scrollToSection]);

  // CRM integrations state
  const [crmIntegrations, setCrmIntegrations] = useState([]);
  const [crmLoading, setCrmLoading] = useState(false);
  const [showCRMModal, setShowCRMModal] = useState(false);
  const [crmDeleting, setCrmDeleting] = useState(null);

  const loadCrmIntegrations = useCallback(() => {
    setCrmLoading(true);
    handleTokenAndPostRequest(`${window.api_host}/get-crm-integrations`, {})
      .then((res) => {
        if (res.data.success) setCrmIntegrations(res.data.integrations || []);
      })
      .catch(() => {})
      .finally(() => setCrmLoading(false));
  }, []);

  const handleDeleteIntegration = async (id) => {
    setCrmDeleting(id);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/delete-crm-integration`,
        { id },
        { "Content-Type": "application/json" }
      );
      setCrmIntegrations((prev) => prev.filter((i) => i.id !== id));
    } catch {}
    setCrmDeleting(null);
  };

  // Close suburb dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (suburbDropdownRef.current && !suburbDropdownRef.current.contains(e.target)) {
        setSuburbDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Lazily fetch the canonical default system prompt so the AI Agent
  // "Restore Default" button has something to drop into the textarea.
  useEffect(() => {
    let cancelled = false;
    setLoadingDefaultPrompt(true);
    handleTokenAndPostRequest(`${window.api_host}/ai-agent/default-system-prompt`)
      .then((res) => {
        if (cancelled) return;
        const prompt = res?.data?.default_system_prompt;
        if (typeof prompt === 'string' && prompt.trim()) {
          setDefaultSystemPrompt(prompt);
        }
      })
      .catch(() => { /* silent — button just stays disabled */ })
      .finally(() => { if (!cancelled) setLoadingDefaultPrompt(false); });
    return () => { cancelled = true; };
  }, []);

  // Fetch billing status so campaign toggles know whether the agent has an
  // active paid subscription. Trialing/paused/free agents see the
  // "sort out payment" modal when they try to enable a campaign.
  useEffect(() => {
    handleTokenAndPostRequest(`${window.api_host}/billing/status`)
      .then((res) => setBillingStatus(res?.data || {}))
      .catch(() => setBillingStatus({ plan: "free" }));
  }, []);

  const handleRestoreDefaultSystemPrompt = () => {
    if (!defaultSystemPrompt) return;
    if (aiGlobalInstructions && aiGlobalInstructions.trim()) {
      const confirmed = window.confirm(
        "Replace the current system prompt with the default? Your existing edits will be lost."
      );
      if (!confirmed) return;
    }
    setAiGlobalInstructions(defaultSystemPrompt);
    setAiAgentSaveMsg("");
  };

  useEffect(() => {
    handleTokenAndPostRequest(`${window.api_host}/authenticate-token-agent`)
      .then((res) => {
        const details = res.data.agent_details || {};
        setAgentDetails(details);
        const vals = {};
        FIELDS.forEach((f) => {
          vals[f.key] = details[f.key] || "";
        });
        setFormValues(vals);
        setOriginalValues(vals);
        // Load saved target suburb IDs and build display objects
        const savedIds = Array.isArray(details.target_suburb_ids) ? details.target_suburb_ids : [];
        setOriginalTargetSuburbIds(savedIds);
        setTargetSuburbs(savedIds.map(id => ({
          value: id,
          type: "suburb",
          label: id.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()).replace(/ Nsw | Vic | Qld | Sa | Wa | Tas | Nt | Act /i, m => " " + m.trim().toUpperCase() + " "),
          state: (id.match(/-([a-z]{2,3})-\d+$/)?.[1] || "").toUpperCase(),
        })));
        // Load notification settings from agent_settings
        let rawSettings = details.agent_settings;
        if (typeof rawSettings === 'string') {
          try { rawSettings = JSON.parse(rawSettings); } catch { rawSettings = {}; }
        }
        const ns = (rawSettings && typeof rawSettings === 'object' ? rawSettings : {}).notification_settings;
        if (ns && typeof ns === 'object') {
          const enabled = ns.enabled !== false;
          setNotifEnabled(enabled);
          setOriginalNotifEnabled(enabled);
          if (ns.event_types && typeof ns.event_types === 'object') {
            const merged = {};
            NOTIFICATION_EVENT_TYPES.forEach(e => {
              merged[e.key] = ns.event_types[e.key] !== false;
            });
            setNotifEventTypes(merged);
            setOriginalNotifEventTypes(merged);
          }
        }

        // Load Platform Config (property-search layout + fields). When the new
        // field-based config isn't present, derive sensible defaults from the
        // legacy delivery_mode so the Settings UI matches the page's current
        // behaviour for this agent.
        const psCfg = (rawSettings && typeof rawSettings === 'object' ? rawSettings : {}).property_search_config;
        const cfg = psCfg && typeof psCfg === 'object' ? psCfg : {};
        const loadedLayout = cfg.layout === 'stepped' ? 'stepped' : 'single';

        const loadedFields = defaultPsFields();
        if (Array.isArray(cfg.fields) && cfg.fields.length > 0) {
          cfg.fields.forEach((f) => {
            if (!f || !loadedFields[f.key]) return;
            loadedFields[f.key] = {
              enabled: f.enabled !== false,
              required: !!f.required,
            };
          });
        } else if (['toggle', 'either', 'both'].includes(cfg.delivery_mode)) {
          // Mirror legacy delivery_mode so the Settings UI shows what the page
          // is currently rendering.
          if (cfg.delivery_mode === 'either') {
            loadedFields.email  = { enabled: true, required: false };
            loadedFields.mobile = { enabled: true, required: false };
          } else if (cfg.delivery_mode === 'both') {
            loadedFields.email  = { enabled: true, required: true };
            loadedFields.mobile = { enabled: true, required: true };
          } // 'toggle' keeps defaults (email required, mobile disabled)
        }

        setPsLayout(loadedLayout);
        setOriginalPsLayout(loadedLayout);
        setPsFields(loadedFields);
        setOriginalPsFields(loadedFields);

        // Load Property Report section visibility. Missing config or missing
        // keys fall back to defaults (days-to-sell/rent off, rest on).
        const reportCfg = (rawSettings && typeof rawSettings === 'object' ? rawSettings : {}).property_report_config;
        const loadedSections = defaultReportSections();
        if (reportCfg && typeof reportCfg === 'object' && Array.isArray(reportCfg.sections)) {
          reportCfg.sections.forEach((s) => {
            if (s && typeof s.key === 'string' && Object.prototype.hasOwnProperty.call(loadedSections, s.key)) {
              loadedSections[s.key] = s.enabled !== false;
            }
          });
        }
        setReportSections(loadedSections);
        setOriginalReportSections(loadedSections);

        // Load outbound campaign toggles from activated_features
        let rawFeatures = details.activated_features;
        if (typeof rawFeatures === 'string') {
          try { rawFeatures = JSON.parse(rawFeatures); } catch { rawFeatures = {}; }
        }
        if (rawFeatures && typeof rawFeatures === 'object') {
          const mergedJobs = {};
          const mergedLimits = {};
          JOB_CAMPAIGNS.forEach(j => {
            const entry = rawFeatures[j.key];
            // Default ON unless explicitly { enabled: false }
            mergedJobs[j.key] = !(entry && typeof entry === 'object' && entry.enabled === false);
            if (j.isSms) {
              const raw = entry && typeof entry === 'object' ? entry.daily_limit : undefined;
              const parsed = Number(raw);
              mergedLimits[j.key] = Number.isFinite(parsed) && parsed >= 0
                ? Math.floor(parsed)
                : SMS_DAILY_LIMIT_DEFAULT;
            }
          });
          setJobSettings(mergedJobs);
          setOriginalJobSettings(mergedJobs);
          setJobDailyLimits(mergedLimits);
          setOriginalJobDailyLimits(mergedLimits);
        }
        // Account credit balance (cents) — read-only display
        const creditRaw = Number(details.balance_cents);
        setBalanceCents(Number.isFinite(creditRaw) ? creditRaw : 0);

        // Load AI agent config
        const aiCfg = (rawSettings && typeof rawSettings === 'object' ? rawSettings : {}).ai_agent_config || {};
        const aiName = details.ai_agent_name || "";
        const aiFirst = details.ai_agent_first_name || "";
        const fullAiEmail = details.ai_agent_email || "";
        const customDomain = (details.custom_domain || "").trim();
        const domain = customDomain || "homeprice.site";
        let prefix = "";
        if (fullAiEmail && fullAiEmail.includes("@")) {
          prefix = fullAiEmail.split("@")[0];
        }
        // On the shared homeprice.site domain we suffix with `.{agent_id}` to
        // guarantee uniqueness. Hide that suffix from the user when displaying
        // the editable prefix so they only see the part they actually chose.
        if (!customDomain && details.agent_id) {
          const idSuffix = `.${details.agent_id}`;
          if (prefix.toLowerCase().endsWith(idSuffix.toLowerCase())) {
            prefix = prefix.slice(0, prefix.length - idSuffix.length);
          }
        }
        const auto = aiCfg.auto_reply === true;
        // Prefer the new 3-way key; fall back to the legacy boolean so existing
        // agents who toggled it on before the review-mode rollout keep their setting.
        const rawMode = (aiCfg.outbound_mode || '').toString().toLowerCase();
        const outboundMode = (rawMode === 'on' || rawMode === 'off' || rawMode === 'review')
          ? rawMode
          : (aiCfg.outbound_campaigns_enabled === true ? 'on' : 'off');
        const globalInstr = typeof aiCfg.global_instructions === 'string' ? aiCfg.global_instructions : "";
        const defaultTitle = `Associate to ${details.agent_name || ""}`.trim();
        const titleVal = typeof aiCfg.ai_title === 'string' && aiCfg.ai_title.trim() !== ''
          ? aiCfg.ai_title
          : defaultTitle;
        setAiAgentName(aiName);
        setAiAgentFirstName(aiFirst);
        setAiAgentEmailPrefix(prefix);
        setAiAgentDomain(domain);
        setAiAgentPhoto(typeof aiCfg.profile_photo === 'string' ? aiCfg.profile_photo : "");
        setAiTitle(titleVal);
        setAiAutoReply(auto);
        setAiOutboundMode(outboundMode);
        setAiGlobalInstructions(globalInstr);
        const defaultSignature = [
          "Kind regards,",
          "",
          "{{ai_agent_name}}",
          "{{ai_title}}",
          "{{agency_name}}",
        ].join("\n");
        const sigVal = typeof aiCfg.email_signature === 'string' && aiCfg.email_signature.trim() !== ''
          ? aiCfg.email_signature
          : defaultSignature;
        setAiEmailSignature(sigVal);
        const sigLogoVal = typeof aiCfg.email_signature_logo === 'string' ? aiCfg.email_signature_logo : "";
        setAiSignatureLogo(sigLogoVal);
        setTestEmailTo((prev) => (testEmailEdited && prev ? prev : (details?.agent_email || "")));
        // Pre-fill the campaign sample fields with the agent's own email.
        setSamplePropertyEmail((prev) => prev || details?.agent_email || "");
        setSampleMarketEmail((prev) => prev || details?.agent_email || "");
        // Pre-fill the campaign SMS sample fields + the mobile editor with the
        // agent's own number (stored in +61 format).
        setSamplePropertySms((prev) => prev || details?.mobile || "");
        setSampleMarketSms((prev) => prev || details?.mobile || "");
        setCampaignMobile(details?.mobile || "");
        setOriginalCampaignMobile(details?.mobile || "");
        setOriginalAiAgent({
          name: aiName, first_name: aiFirst, email_prefix: prefix, title: titleVal,
          auto_reply: auto, outbound_mode: outboundMode,
          global_instructions: globalInstr, email_signature: sigVal, signature_logo: sigLogoVal,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    // Load Facebook connection status
    handleTokenAndPostRequest(`${window.api_host}/facebook-status`)
      .then((res) => {
        if (res.data.connected) {
          setFbConnected(true);
          setFbPageNames((res.data.pages || []).map(p => p.page_name));
        }
      })
      .catch(() => {});

    // Load CRM integrations
    loadCrmIntegrations();
  }, []);

  // Suburb search (debounced, suburbs only — no postcodes)
  const searchSuburbs = useCallback((query) => {
    if (suburbSearchTimer.current) clearTimeout(suburbSearchTimer.current);
    if (!query.trim()) {
      setSuburbResults([]);
      setSuburbDropdownOpen(false);
      return;
    }
    suburbSearchTimer.current = setTimeout(async () => {
      setSuburbSearching(true);
      try {
        const res = await fetch(
          `${window.api_host}/location-search?q=${encodeURIComponent(query)}`
        );
        const data = await res.json();
        const filtered = (Array.isArray(data) ? data : []).filter(
          item => item.type !== "postcode" && !targetSuburbs.some(s => s.value === item.value)
        );
        setSuburbResults(filtered);
        setSuburbDropdownOpen(filtered.length > 0);
      } catch (e) {
        setSuburbResults([]);
      }
      setSuburbSearching(false);
    }, 300);
  }, [targetSuburbs]);

  const handleSelectSuburb = (item) => {
    if (targetSuburbs.some(s => s.value === item.value)) return;
    setTargetSuburbs(prev => [...prev, item]);
    setSuburbQuery("");
    const remaining = suburbResults.filter(r => r.value !== item.value);
    setSuburbResults(remaining);
    if (remaining.length === 0) setSuburbDropdownOpen(false);
    if (suburbInputRef.current) suburbInputRef.current.focus();
    setSuburbSaveMsg("");
  };

  const handleRemoveSuburb = (idx) => {
    setTargetSuburbs(prev => prev.filter((_, i) => i !== idx));
    setSuburbSaveMsg("");
  };

  const suburbIdsChanged = (() => {
    const currentIds = targetSuburbs.map(s => s.value).sort().join(",");
    const originalIds = [...originalTargetSuburbIds].sort().join(",");
    return currentIds !== originalIds;
  })();

  const handleSaveSuburbs = async () => {
    setSavingSuburbs(true);
    setSuburbSaveMsg("");
    try {
      const ids = targetSuburbs.map(s => s.value);
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-details`,
        { target_suburb_ids: ids },
        { "Content-Type": "application/json" }
      );
      if (res.data.success) {
        setSuburbSaveMsg("Target suburbs saved successfully.");
        setOriginalTargetSuburbIds(ids);
      } else {
        setSuburbSaveMsg(res.data.message || "Failed to save target suburbs.");
      }
    } catch {
      setSuburbSaveMsg("Failed to save target suburbs. Please try again.");
    } finally {
      setSavingSuburbs(false);
    }
  };

  // --- Notification Settings ---
  const notifHasChanges = notifEnabled !== originalNotifEnabled ||
    NOTIFICATION_EVENT_TYPES.some(e => notifEventTypes[e.key] !== originalNotifEventTypes[e.key]);

  const handleSaveNotifSettings = async () => {
    setSavingNotif(true);
    setNotifSaveMsg("");
    try {
      const notificationSettings = {
        enabled: notifEnabled,
        event_types: { ...notifEventTypes },
      };
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-details`,
        { agent_settings: { notification_settings: notificationSettings } },
        { "Content-Type": "application/json" }
      );
      if (res.data.success) {
        setNotifSaveMsg("Notification settings saved successfully.");
        setOriginalNotifEnabled(notifEnabled);
        setOriginalNotifEventTypes({ ...notifEventTypes });
      } else {
        setNotifSaveMsg(res.data.message || "Failed to save notification settings.");
      }
    } catch {
      setNotifSaveMsg("Failed to save notification settings. Please try again.");
    } finally {
      setSavingNotif(false);
    }
  };

  // --- Platform Config (property-search layout + fields + report sections) ---
  const platformConfigHasChanges =
    psLayout !== originalPsLayout
    || JSON.stringify(psFields) !== JSON.stringify(originalPsFields)
    || JSON.stringify(reportSections) !== JSON.stringify(originalReportSections);

  // At least one of email/mobile must be enabled — otherwise the visitor has
  // nowhere to receive the report.
  const platformConfigValid = !!(psFields.email?.enabled || psFields.mobile?.enabled);

  const togglePsField = (key, patch) => {
    setPsFields((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    setPlatformConfigMsg("");
  };

  const toggleReportSection = (key, enabled) => {
    setReportSections((prev) => ({ ...prev, [key]: enabled }));
    setPlatformConfigMsg("");
  };

  const handleSavePlatformConfig = async () => {
    if (!platformConfigValid) {
      setPlatformConfigMsg("Enable at least one of Email or Mobile.");
      return;
    }
    setSavingPlatformConfig(true);
    setPlatformConfigMsg("");
    try {
      const fieldsArr = PS_FIELD_DEFS.map((f) => ({
        key: f.key,
        enabled: !!psFields[f.key]?.enabled,
        required: !!psFields[f.key]?.enabled && !!psFields[f.key]?.required,
      }));
      const sectionsArr = PR_SECTION_DEFS.map((s) => ({
        key: s.key,
        enabled: reportSections[s.key] !== false,
      }));
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-details`,
        { agent_settings: {
            property_search_config: { layout: psLayout, fields: fieldsArr },
            property_report_config: { sections: sectionsArr },
        } },
        { "Content-Type": "application/json" }
      );
      if (res.data.success) {
        setPlatformConfigMsg("Platform config saved successfully.");
        setOriginalPsLayout(psLayout);
        setOriginalPsFields(psFields);
        setOriginalReportSections(reportSections);
      } else {
        setPlatformConfigMsg(res.data.message || "Failed to save platform config.");
      }
    } catch {
      setPlatformConfigMsg("Failed to save platform config. Please try again.");
    } finally {
      setSavingPlatformConfig(false);
    }
  };

  // Public URL of the agent's /property-search landing page. On a custom domain
  // the page resolves the agent from the host, so no ?agent= param is needed;
  // otherwise we deep-link via the shared homeprice.site domain with the slug.
  const propertySearchUrl = (() => {
    const customDomain = (agentDetails?.custom_domain || "").trim();
    if (customDomain) {
      const host = customDomain.replace(/^https?:\/\//, "").replace(/\/+$/, "");
      return `https://${host}/property-search`;
    }
    const slug = agentDetails?.landing_page_slug || "";
    return `https://www.homeprice.site/property-search${slug ? `?agent=${encodeURIComponent(slug)}` : ""}`;
  })();

  // --- Outbound Campaign Job Toggles ---
  const jobsHaveChanges = JOB_CAMPAIGNS.some(j => jobSettings[j.key] !== originalJobSettings[j.key])
    || JOB_CAMPAIGNS.filter(j => j.isSms).some(j => jobDailyLimits[j.key] !== originalJobDailyLimits[j.key]);

  const handleSaveJobSettings = async () => {
    setSavingJobs(true);
    setJobsSaveMsg("");
    try {
      // Build activated_features patch in { <key>: { enabled: bool } } shape.
      // Backend shallow-merges into the existing JSONB column, so other keys
      // (e.g. sms_property_updates) are preserved.
      const featuresPatch = {};
      JOB_CAMPAIGNS.forEach(j => {
        const obj = { enabled: jobSettings[j.key] !== false };
        if (j.isSms) {
          const v = Number(jobDailyLimits[j.key]);
          obj.daily_limit = Number.isFinite(v) && v >= 0 ? Math.floor(v) : SMS_DAILY_LIMIT_DEFAULT;
        }
        featuresPatch[j.key] = obj;
      });
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-details`,
        { activated_features: featuresPatch },
        { "Content-Type": "application/json" }
      );
      if (res.data.success) {
        setJobsSaveMsg("Campaign preferences saved successfully.");
        setOriginalJobSettings({ ...jobSettings });
        setOriginalJobDailyLimits({ ...jobDailyLimits });
      } else {
        setJobsSaveMsg(res.data.message || "Failed to save campaign preferences.");
      }
    } catch {
      setJobsSaveMsg("Failed to save campaign preferences. Please try again.");
    } finally {
      setSavingJobs(false);
    }
  };

  // --- Send sample campaign emails ---
  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  // The fixed property used for property-update samples (set by product).
  const SAMPLE_PROPERTY_ID = "vl3272ep";
  const SAMPLE_PROPERTY_SLUG = "22-sevira-avenue-gledswood-hills-nsw-2557";

  const handleSendPropertySample = async () => {
    const to = samplePropertyEmail.trim();
    if (!EMAIL_RE.test(to)) {
      setSamplePropertyMsg("Enter a valid email address.");
      return;
    }
    setSamplePropertySending(true);
    setSamplePropertyMsg("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/send-property-report`,
        {
          email: to,
          property_id: SAMPLE_PROPERTY_ID,
          property_slug: SAMPLE_PROPERTY_SLUG,
          agency_id: agentDetails?.agency_id,
          agent_id: agentDetails?.agent_id,
          agent_slug: agentDetails?.landing_page_slug,
          report_type: "property_update",
          source: "settings_sample_send",
        },
        { "Content-Type": "application/json" }
      );
      const ok = res?.status === 200 && !res?.data?.error;
      setSamplePropertyMsg(ok ? `Sample sent to ${to}.` : (res?.data?.error || "Failed to send sample."));
    } catch {
      setSamplePropertyMsg("Failed to send sample. Please try again.");
    } finally {
      setSamplePropertySending(false);
    }
  };

  const handleSendMarketSample = async () => {
    const to = sampleMarketEmail.trim();
    if (!EMAIL_RE.test(to)) {
      setSampleMarketMsg("Enter a valid email address.");
      return;
    }
    setSampleMarketSending(true);
    setSampleMarketMsg("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/send-market-update-sample`,
        { sample_email: to },
        { "Content-Type": "application/json" }
      );
      const ok = res?.data?.status === "success";
      setSampleMarketMsg(ok ? `Sample sent to ${to}.` : (res?.data?.message || "Failed to send sample."));
    } catch {
      setSampleMarketMsg("Failed to send sample. Please try again.");
    } finally {
      setSampleMarketSending(false);
    }
  };

  // --- Send sample campaign SMS ---
  // Accepts AU mobiles in local (04xx) or +61 format. Two versions (intro +
  // update) are sent server-side for each campaign.
  const MOBILE_RE = /^(\+?61|0)4\d{2}\s?\d{3}\s?\d{3}$/;

  // Normalise an AU mobile to E.164 (+61...) for storage / sending.
  const toE164Mobile = (value) => {
    const digits = (value || "").replace(/[\s\-()]/g, "");
    if (/^04\d{8}$/.test(digits)) return "+61" + digits.slice(1);
    if (/^\+?614\d{8}$/.test(digits)) return digits.startsWith("+") ? digits : "+" + digits;
    if (/^614\d{8}$/.test(digits)) return "+" + digits;
    return value.trim();
  };

  const handleSendSmsSample = async ({ endpoint, value, setMsg, setSending }) => {
    const to = (value || "").trim();
    if (!MOBILE_RE.test(to.replace(/\s/g, ""))) {
      setMsg("Enter a valid Australian mobile (e.g. 0421 346 175).");
      return;
    }
    setSending(true);
    setMsg("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/${endpoint}`,
        { mobile: to },
        { "Content-Type": "application/json" }
      );
      const ok = res?.data?.status === "success";
      setMsg(ok ? `Sample sent to ${to}.` : (res?.data?.message || "Failed to send sample."));
    } catch {
      setMsg("Failed to send sample. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleSendPropertySmsSample = () => handleSendSmsSample({
    endpoint: "send-property-update-sms-sample",
    value: samplePropertySms,
    setMsg: setSamplePropertySmsMsg,
    setSending: setSamplePropertySmsSending,
  });

  const handleSendMarketSmsSample = () => handleSendSmsSample({
    endpoint: "send-market-update-sms-sample",
    value: sampleMarketSms,
    setMsg: setSampleMarketSmsMsg,
    setSending: setSampleMarketSmsSending,
  });

  // --- Save the agent's mobile number (Campaigns top input) ---
  const mobileHasChanges = campaignMobile.trim() !== (originalCampaignMobile || "").trim();

  const handleSaveMobile = async () => {
    const raw = campaignMobile.trim();
    if (raw && !MOBILE_RE.test(raw.replace(/\s/g, ""))) {
      setMobileSaveMsg("Enter a valid Australian mobile (e.g. 0421 346 175).");
      return;
    }
    // Store in +61 format to match the agent_details.mobile convention.
    const normalised = raw ? toE164Mobile(raw) : "";
    setSavingMobile(true);
    setMobileSaveMsg("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-details`,
        { mobile: normalised },
        { "Content-Type": "application/json" }
      );
      const ok = res?.status === 200 && !res?.data?.error;
      if (ok) {
        setCampaignMobile(normalised);
        setOriginalCampaignMobile(normalised);
        setAgentDetails((prev) => (prev ? { ...prev, mobile: normalised } : prev));
        // Keep the SMS sample fields in sync with the freshly saved number.
        setSamplePropertySms(normalised);
        setSampleMarketSms(normalised);
        setMobileSaveMsg("Mobile number saved.");
      } else {
        setMobileSaveMsg(res?.data?.error || res?.data?.message || "Failed to save number.");
      }
    } catch {
      setMobileSaveMsg("Failed to save number. Please try again.");
    } finally {
      setSavingMobile(false);
    }
  };

  // --- AI Agent Config ---
  const aiAgentHasChanges = (
    aiAgentName !== originalAiAgent.name ||
    aiAgentFirstName !== originalAiAgent.first_name ||
    aiAgentEmailPrefix !== originalAiAgent.email_prefix ||
    aiTitle !== originalAiAgent.title ||
    aiAutoReply !== originalAiAgent.auto_reply ||
    aiOutboundMode !== originalAiAgent.outbound_mode ||
    aiGlobalInstructions !== originalAiAgent.global_instructions ||
    aiEmailSignature !== originalAiAgent.email_signature ||
    aiSignatureLogo !== originalAiAgent.signature_logo
  );

  const handleSaveAiAgent = async () => {
    setSavingAiAgent(true);
    setAiAgentSaveMsg("");
    try {
      const trimmedPrefix = (aiAgentEmailPrefix || "").trim().toLowerCase();
      if (trimmedPrefix && !/^[a-z0-9._+-]+$/.test(trimmedPrefix)) {
        setAiAgentSaveMsg("Invalid email prefix. Use letters, numbers, dots, +, - or _.");
        setSavingAiAgent(false);
        return;
      }
      // On the shared homeprice.site domain, append `.{agent_id}` to the prefix
      // before saving so the resolved address is unique across all agents.
      const onSharedDomain = aiAgentDomain === "homeprice.site";
      const agentIdForSuffix = agentDetails?.agent_id;
      const prefixToSave = (onSharedDomain && trimmedPrefix && agentIdForSuffix)
        ? `${trimmedPrefix}.${agentIdForSuffix}`
        : trimmedPrefix;
      const payload = {
        ai_agent_name: aiAgentName.trim(),
        ai_agent_first_name: aiAgentFirstName.trim(),
        ai_agent_email_prefix: prefixToSave,
        agent_settings: {
          ai_agent_config: {
            auto_reply: !!aiAutoReply,
            outbound_mode: aiOutboundMode,
            // Keep the legacy boolean in sync so any other batch jobs that
            // still read the old key (and weren't updated for the 3-way mode)
            // continue to behave correctly: any non-off mode means sends are
            // permitted in some form.
            outbound_campaigns_enabled: aiOutboundMode !== 'off',
            global_instructions: aiGlobalInstructions,
            ai_title: aiTitle,
            email_signature: aiEmailSignature,
            email_signature_logo: aiSignatureLogo || "",
            // Preserve the uploaded profile photo — the backend uses a shallow
            // jsonb || merge, so omitting this key would wipe it out.
            profile_photo: aiAgentPhoto || "",
          },
        },
      };
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-details`,
        payload,
        { "Content-Type": "application/json" }
      );
      if (res.data.success) {
        setAiAgentSaveMsg("AI agent settings saved successfully.");
        setOriginalAiAgent({
          name: aiAgentName,
          first_name: aiAgentFirstName,
          email_prefix: trimmedPrefix,
          title: aiTitle,
          auto_reply: !!aiAutoReply,
          outbound_mode: aiOutboundMode,
          global_instructions: aiGlobalInstructions,
          email_signature: aiEmailSignature,
          signature_logo: aiSignatureLogo,
        });
      } else {
        setAiAgentSaveMsg(res.data.message || "Failed to save AI agent settings.");
      }
    } catch {
      setAiAgentSaveMsg("Failed to save AI agent settings. Please try again.");
    } finally {
      setSavingAiAgent(false);
    }
  };

  const handleSendTestEmail = async () => {
    const to = (testEmailTo || "").trim();
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      setTestEmailMsg("Enter a valid email address.");
      return;
    }
    setSendingTestEmail(true);
    setTestEmailMsg("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/ai-agent/send-test-email`,
        { to_email: to },
        { "Content-Type": "application/json" }
      );
      if (res?.data?.status === "success") {
        setTestEmailMsg(`Sent to ${res.data.sent_to}. Check your inbox.`);
      } else {
        setTestEmailMsg(res?.data?.message || "Failed to send test email.");
      }
    } catch (err) {
      const apiMsg = err?.response?.data?.message;
      setTestEmailMsg(apiMsg || "Failed to send test email. Please try again.");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleAiPhotoUpload = async (file) => {
    if (!file) return;
    setUploadingAiPhoto(true);
    setAiAgentSaveMsg("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("field", "ai_agent_profile_image");
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-image`,
        formData
      );
      if (res.data.success && res.data.url) {
        setAiAgentPhoto(res.data.url);
      } else {
        setAiAgentSaveMsg(res.data.message || "Failed to upload photo.");
      }
    } catch {
      setAiAgentSaveMsg("Failed to upload photo. Please try again.");
    } finally {
      setUploadingAiPhoto(false);
    }
  };

  const handleSignatureLogoUpload = async (file) => {
    if (!file) return;
    setUploadingSignatureLogo(true);
    setAiAgentSaveMsg("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("field", "ai_agent_signature_logo");
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-image`,
        formData
      );
      if (res.data.success && res.data.url) {
        setAiSignatureLogo(res.data.url);
      } else {
        setAiAgentSaveMsg(res.data.message || "Failed to upload signature logo.");
      }
    } catch {
      setAiAgentSaveMsg("Failed to upload signature logo. Please try again.");
    } finally {
      setUploadingSignatureLogo(false);
    }
  };

  // --- Facebook OAuth ---
  const handleFacebookConnect = async () => {
    setFbLoading(true);
    setFbMsg("");
    try {
      const res = await handleTokenAndPostRequest(`${window.api_host}/facebook-auth-url`, {});
      if (res.data.success && res.data.url) {
        // Open Facebook OAuth in a popup
        const width = 600, height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popup = window.open(
          res.data.url,
          "fb_oauth",
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
        );
        fbPopupRef.current = popup;

        // Clear any stale code and poll localStorage for the OAuth code
        localStorage.removeItem("fb_oauth_code");
        const pollTimer = setInterval(() => {
          if (!popup || popup.closed) {
            clearInterval(pollTimer);
            // Check one last time after close
            const code = localStorage.getItem("fb_oauth_code");
            localStorage.removeItem("fb_oauth_code");
            if (code) {
              exchangeFacebookCode(code);
            } else {
              setFbLoading(false);
            }
            return;
          }
          const code = localStorage.getItem("fb_oauth_code");
          if (code) {
            clearInterval(pollTimer);
            localStorage.removeItem("fb_oauth_code");
            if (!popup.closed) popup.close();
            exchangeFacebookCode(code);
          }
        }, 500);
      } else {
        setFbMsg("Failed to start Facebook login.");
        setFbLoading(false);
      }
    } catch {
      setFbMsg("Failed to start Facebook login.");
      setFbLoading(false);
    }
  };

  const exchangeFacebookCode = async (code) => {
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/facebook-callback`,
        { code },
        { "Content-Type": "application/json" }
      );
      if (res.data.success && res.data.pages) {
        // Save all pages at once
        await saveAllFacebookPages(res.data.pages);
      } else {
        setFbMsg(res.data.message || "Failed to connect Facebook.");
        setFbLoading(false);
      }
    } catch {
      setFbMsg("Failed to connect Facebook.");
      setFbLoading(false);
    }
  };

  const saveAllFacebookPages = async (pages) => {
    setFbLoading(true);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/facebook-save-page`,
        { pages: pages.map(p => ({ id: p.id, name: p.name, access_token: p.access_token })) },
        { "Content-Type": "application/json" }
      );
      if (res.data.success) {
        setFbConnected(true);
        setFbPageNames((res.data.pages || []).map(p => p.page_name));
        const names = (res.data.pages || []).map(p => p.page_name).join(", ");
        setFbMsg(`Connected: ${names}`);
      } else {
        setFbMsg(res.data.message || "Failed to save Facebook pages.");
      }
    } catch {
      setFbMsg("Failed to save Facebook pages.");
    } finally {
      setFbLoading(false);
    }
  };

  const handleFacebookDisconnect = async () => {
    setFbLoading(true);
    setFbMsg("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/facebook-disconnect`,
        {},
        { "Content-Type": "application/json" }
      );
      if (res.data.success) {
        setFbConnected(false);
        setFbPageNames([]);
        setFbMsg("Facebook disconnected.");
      } else {
        setFbMsg(res.data.message || "Failed to disconnect.");
      }
    } catch {
      setFbMsg("Failed to disconnect Facebook.");
    } finally {
      setFbLoading(false);
    }
  };

  const handleChange = (key, value) => {
    if (LOCKED_FIELDS.has(key)) return;
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setSaveMsg("");
  };

  const handleFontUpload = async (key, file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["otf", "ttf", "woff", "woff2"].includes(ext)) {
      setSaveMsg("Invalid font file. Allowed: OTF, TTF, WOFF, WOFF2");
      return;
    }
    setUploadingField(key);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("field", key);
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-image`,
        formData
      );
      if (res.data.success && res.data.url) {
        handleChange(key, res.data.url);
        setOriginalValues((prev) => ({ ...prev, [key]: res.data.url }));
      } else {
        setSaveMsg(res.data.message || "Failed to upload font.");
      }
    } catch {
      setSaveMsg("Failed to upload font. Please try again.");
    } finally {
      setUploadingField(null);
    }
  };

  const handleImageUpload = async (key, file) => {
    if (!file) return;
    setUploadingField(key);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("field", key);
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-image`,
        formData
      );
      if (res.data.success && res.data.url) {
        handleChange(key, res.data.url);
        setOriginalValues((prev) => ({ ...prev, [key]: res.data.url }));
      }
    } catch {
      setSaveMsg("Failed to upload image. Please try again.");
    } finally {
      setUploadingField(null);
    }
  };

  const hasChanges = FIELDS.some(
    (f) => f.type !== "image" && f.type !== "font" && !LOCKED_FIELDS.has(f.key) && formValues[f.key] !== originalValues[f.key]
  );

  const getChangedFields = () =>
    FIELDS.filter(
      (f) => f.type !== "image" && f.type !== "font" && !LOCKED_FIELDS.has(f.key) && formValues[f.key] !== originalValues[f.key]
    );

  const handleSave = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    setSaveMsg("");
    try {
      const updates = {};
      FIELDS.forEach((f) => {
        if (f.type !== "image" && f.type !== "font" && !LOCKED_FIELDS.has(f.key) && formValues[f.key] !== originalValues[f.key]) {
          updates[f.key] = formValues[f.key];
        }
      });
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-details`,
        updates,
        { "Content-Type": "application/json" }
      );
      if (res.data.success) {
        setSaveMsg("Settings saved successfully.");
        setOriginalValues({ ...formValues });
        // Update cached user data
        try {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          user.agent_details = { ...user.agent_details, ...updates };
          if (updates.agent_name) user.name = updates.agent_name;
          localStorage.setItem("user", JSON.stringify(user));
        } catch {}
      } else {
        setSaveMsg(res.data.message || "Failed to save settings.");
      }
    } catch {
      setSaveMsg("Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormValues({ ...originalValues });
    setSaveMsg("");
  };

  const sectionHasChanges = (fieldKeys) =>
    fieldKeys.some(
      (k) => !LOCKED_FIELDS.has(k) && formValues[k] !== originalValues[k]
    );

  const handleSaveSection = async (sectionKey, fieldKeys) => {
    setSavingSection(sectionKey);
    setSectionSaveMsgs((prev) => ({ ...prev, [sectionKey]: "" }));
    try {
      const updates = {};
      fieldKeys.forEach((k) => {
        if (!LOCKED_FIELDS.has(k) && formValues[k] !== originalValues[k]) {
          updates[k] = formValues[k];
        }
      });
      if (Object.keys(updates).length === 0) {
        setSavingSection(null);
        return;
      }
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-details`,
        updates,
        { "Content-Type": "application/json" }
      );
      if (res.data.success) {
        setSectionSaveMsgs((prev) => ({ ...prev, [sectionKey]: "Saved successfully." }));
        setOriginalValues((prev) => ({ ...prev, ...updates }));
        try {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          user.agent_details = { ...user.agent_details, ...updates };
          if (updates.agent_name) user.name = updates.agent_name;
          localStorage.setItem("user", JSON.stringify(user));
        } catch {}
      } else {
        setSectionSaveMsgs((prev) => ({ ...prev, [sectionKey]: res.data.message || "Failed to save." }));
      }
    } catch {
      setSectionSaveMsgs((prev) => ({ ...prev, [sectionKey]: "Failed to save. Please try again." }));
    } finally {
      setSavingSection(null);
    }
  };

  const SectionSaveBar = ({ sectionKey, fieldKeys }) => {
    const changed = sectionHasChanges(fieldKeys);
    const isSaving = savingSection === sectionKey;
    const msg = sectionSaveMsgs[sectionKey];
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginTop: 8,
          justifyContent: "flex-end",
        }}
      >
        {msg && (
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              marginRight: "auto",
              color: msg.toLowerCase().includes("success") ? "#10b981" : "#e53935",
            }}
          >
            {msg}
          </span>
        )}
        <button
          onClick={() => handleSaveSection(sectionKey, fieldKeys)}
          disabled={!changed || isSaving}
          style={{
            padding: "7px 18px",
            borderRadius: 8,
            border: "none",
            background: changed
              ? "linear-gradient(90deg, #1a3f6b, #10b981)"
              : "#d1d5db",
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            cursor: changed && !isSaving ? "pointer" : "default",
            display: "flex",
            alignItems: "center",
            gap: 6,
            transition: "background 0.15s",
          }}
        >
          {isSaving ? (
            <><InlineSpinner size={12} color="#fff" /> Saving...</>
          ) : (
            <><FaSave size={11} /> Save Changes</>
          )}
        </button>
      </div>
    );
  };

  const handleSavePassword = async () => {
    setPasswordMsg('');
    if (!newPassword || newPassword.length < 8) {
      setPasswordMsg('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg('Passwords do not match.');
      return;
    }
    setPasswordLoading(true);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/set-password-agent`,
        { new_password: newPassword },
        { 'Content-Type': 'application/json' }
      );
      if (res.data.success) {
        setPasswordMsg('Password updated successfully.');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordMsg(res.data.message || 'Failed to update password.');
      }
    } catch {
      setPasswordMsg('Failed to update password. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ flex: 1, overflowY: "auto", background: "#f6f8fa" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", padding: isMobile ? "72px 16px 64px" : "32px 24px 64px" }}>
          <Skeleton width={120} height={28} style={{ marginBottom: 4 }} />
          <Skeleton width="80%" height={13} style={{ marginBottom: 32 }} />
          {/* Profile section skeleton */}
          {[...Array(3)].map((_, s) => (
            <div key={s} style={{ background: "#fff", borderRadius: 12, padding: 20, marginBottom: 20, border: "1px solid #e5e7eb" }}>
              <Skeleton width={160} height={16} style={{ marginBottom: 16 }} />
              {[...Array(3)].map((_, f) => (
                <div key={f} style={{ marginBottom: 14 }}>
                  <Skeleton width={100} height={12} style={{ marginBottom: 6 }} />
                  <Skeleton height={38} borderRadius={8} />
                </div>
              ))}
            </div>
          ))}
          {/* Branding section skeleton */}
          <div style={{ background: "#fff", borderRadius: 12, padding: 20, marginBottom: 20, border: "1px solid #e5e7eb" }}>
            <Skeleton width={180} height={16} style={{ marginBottom: 16 }} />
            <div style={{ display: "flex", gap: 16, marginBottom: 14 }}>
              <Skeleton width={80} height={80} borderRadius={10} />
              <Skeleton width={80} height={80} borderRadius={10} />
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <Skeleton width={40} height={40} borderRadius={8} />
              <Skeleton width={40} height={40} borderRadius={8} />
            </div>
          </div>
          {/* Save bar skeleton */}
          <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
            <Skeleton width={100} height={40} borderRadius={8} />
            <Skeleton width={80} height={40} borderRadius={8} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} style={{ flex: 1, overflowY: "auto", background: "#f6f8fa" }}>
      <style>{SPINNER_KEYFRAMES}</style>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: isMobile ? "72px 16px 64px" : "32px 24px 64px" }}>
        <h1 style={{ fontSize: isMobile ? 26 : 32, fontWeight: 700, color: "#1f2937", marginBottom: 4 }}>Settings</h1>
        <p style={{ fontSize: 14, color: "#666", marginBottom: 16, marginTop: 0 }}>
          Manage your profile, branding, and platform defaults. These values are used across your landing page, property reports, email notifications, and AI-generated content.
        </p>

        {/* Sticky scrollable tab bar */}
        <div style={{
          position: "sticky", top: isMobile ? 56 : 0, zIndex: 20,
          background: "#f6f8fa", paddingBottom: 0, marginBottom: 16,
        }}>
          <div style={{ position: "relative" }}>
          {/* Left/right edge fades hint that the tab row scrolls horizontally. */}
          <div aria-hidden onClick={() => tabsRowRef.current?.scrollBy({ left: -200, behavior: "smooth" })} style={{
            position: "absolute", top: 0, bottom: 1, left: 0, width: 44,
            borderTopLeftRadius: 10, pointerEvents: tabOverflow.left ? "auto" : "none", zIndex: 2,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "flex-start",
            paddingLeft: 6, color: "#9ca3af", fontSize: 16,
            background: "linear-gradient(90deg, #fff 30%, rgba(255,255,255,0))",
            opacity: tabOverflow.left ? 1 : 0, transition: "opacity 0.2s",
          }}>‹</div>
          <div aria-hidden onClick={() => tabsRowRef.current?.scrollBy({ left: 200, behavior: "smooth" })} style={{
            position: "absolute", top: 0, bottom: 1, right: 0, width: 44,
            borderTopRightRadius: 10, pointerEvents: tabOverflow.right ? "auto" : "none", zIndex: 2,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "flex-end",
            paddingRight: 6, color: "#9ca3af", fontSize: 16,
            background: "linear-gradient(270deg, #fff 30%, rgba(255,255,255,0))",
            opacity: tabOverflow.right ? 1 : 0, transition: "opacity 0.2s",
          }}>›</div>
          <div ref={tabsRowRef} className="settings-tabs-row" style={{
            display: "flex", gap: 0, overflowX: "auto", scrollbarWidth: "none",
            borderBottom: "1px solid #e5e7eb", background: "#fff", borderRadius: "10px 10px 0 0",
            WebkitOverflowScrolling: "touch",
          }}>
            <style>{`.settings-tabs-row::-webkit-scrollbar { display: none; }`}</style>
            {SECTIONS.map(s => (
              <button
                key={s.key}
                ref={el => { tabButtonRefs.current[s.key] = el; }}
                onClick={() => handleTabClick(s.key)}
                style={{
                  flex: "0 0 auto", padding: "11px 16px", background: "transparent",
                  border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                  whiteSpace: "nowrap", transition: "color 0.2s, border-color 0.2s",
                  color: activeSection === s.key ? "#1a3f6b" : "#9ca3af",
                  borderBottom: activeSection === s.key ? "2px solid #1a3f6b" : "2px solid transparent",
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
          </div>
        </div>

        {/* Profile & Identity */}
        <div ref={el => { sectionRefs.current.profile = el; }}>
        <SettingsSection title="Profile & Identity">
          {FIELDS.filter((f) =>
            ["agent_name", "agent_first_name", "agent_email", "agent_notification_email", "agent_title", "agent_description"].includes(f.key)
          ).map((f) => (
            <SettingsField
              key={f.key}
              field={f}
              value={formValues[f.key]}
              onChange={handleChange}
              isChanged={formValues[f.key] !== originalValues[f.key]}
              isLocked={LOCKED_FIELDS.has(f.key)}
            />
          ))}
          <SectionSaveBar
            sectionKey="profile"
            fieldKeys={["agent_name", "agent_first_name", "agent_email", "agent_notification_email", "agent_title", "agent_description"]}
          />

          {/* Agent mobile number. Used as the default recipient for SMS
              campaign samples and shown on published appraisals. */}
          <div style={{
            padding: "12px 14px", borderRadius: 8,
            border: "1px solid #f0f0f0", background: "#fafafa", marginTop: 12,
          }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
              Your Mobile Number
            </label>
            <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 8px", lineHeight: 1.5 }}>
              Used to send yourself SMS campaign samples and shown on your published appraisals. AU format, e.g. 0421 346 175.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <input
                type="tel"
                value={campaignMobile}
                placeholder="0421 346 175"
                onChange={(e) => { setCampaignMobile(e.target.value); setMobileSaveMsg(""); }}
                onKeyDown={(e) => { if (e.key === "Enter" && mobileHasChanges && !savingMobile) handleSaveMobile(); }}
                style={{
                  flex: 1, minWidth: 200, padding: "8px 12px",
                  borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
              <button
                onClick={handleSaveMobile}
                disabled={!mobileHasChanges || savingMobile}
                style={{
                  padding: "8px 18px", borderRadius: 8, border: "none",
                  background: (mobileHasChanges && !savingMobile) ? "linear-gradient(90deg, #1a3f6b, #10b981)" : "#d1d5db",
                  color: "#fff", fontWeight: 600, fontSize: 14,
                  cursor: (mobileHasChanges && !savingMobile) ? "pointer" : "default",
                  display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                  minWidth: 100, justifyContent: "center",
                }}
              >
                {savingMobile ? <><InlineSpinner size={13} color="#fff" /> Saving...</> : "Save"}
              </button>
            </div>
            {mobileSaveMsg && (
              <div style={{
                fontSize: 12, fontWeight: 500, marginTop: 6,
                color: mobileSaveMsg.includes("saved") ? "#10b981" : "#e53935",
              }}>
                {mobileSaveMsg}
              </div>
            )}
          </div>
        </SettingsSection>
        </div>

        {/* Agency Details */}
        <div ref={el => { sectionRefs.current.agency = el; }}>
        <SettingsSection title="Agency Details">
          {FIELDS.filter((f) =>
            ["agency_name_long", "agency_name_short", "agent_website", "agent_contact_link", "agency_facebook_link", "agency_instagram_link"].includes(f.key)
          ).map((f) => (
            <SettingsField
              key={f.key}
              field={f}
              value={formValues[f.key]}
              onChange={handleChange}
              isChanged={formValues[f.key] !== originalValues[f.key]}
            />
          ))}
          <SectionSaveBar
            sectionKey="agency"
            fieldKeys={["agency_name_long", "agency_name_short", "agent_website", "agent_contact_link", "agency_facebook_link", "agency_instagram_link"]}
          />
        </SettingsSection>
        </div>

        {/* Facebook Integration */}
        {agentDetails?.agent_email?.toLowerCase() === "bayan@vendorleads.com.au" && (
        <SettingsSection title="Facebook Integration">
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <FaFacebook size={18} color="#1877f2" />
              <span style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>
                {fbConnected ? "Connected" : "Connect Your Facebook Page"}
              </span>
              {fbConnected && (
                <span style={{
                  background: "#ecfdf5", color: "#059669", fontSize: 11, fontWeight: 600,
                  padding: "2px 8px", borderRadius: 8,
                }}>Active</span>
              )}
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.5 }}>
              {fbConnected
                ? `Your Facebook Page${fbPageNames.length > 1 ? "s" : ""} ${fbPageNames.map(n => `"${n}"`).join(", ")} ${fbPageNames.length > 1 ? "are" : "is"} connected. AI-generated social blurbs can be posted directly from the Articles and Social pages.`
                : "Connect your Facebook Page to publish AI-generated social blurbs directly from the platform — no copy-pasting needed."
              }
            </p>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {fbConnected ? (
                <button
                  onClick={handleFacebookDisconnect}
                  disabled={fbLoading}
                  style={{
                    padding: "8px 20px", borderRadius: 8, border: "1px solid #fecaca",
                    background: "#fff", color: "#dc2626", fontWeight: 600, fontSize: 14,
                    cursor: fbLoading ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                  }}
                >
                  {fbLoading ? <InlineSpinner size={13} color="#dc2626" /> : <FaTimes size={11} />}
                  Disconnect Facebook
                </button>
              ) : (
                <button
                  onClick={handleFacebookConnect}
                  disabled={fbLoading}
                  style={{
                    padding: "8px 20px", borderRadius: 8, border: "none",
                    background: "#1877f2", color: "#fff", fontWeight: 600, fontSize: 14,
                    cursor: fbLoading ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                  }}
                >
                  {fbLoading ? <InlineSpinner size={13} color="#fff" /> : <FaFacebook size={13} />}
                  Connect Facebook Page
                </button>
              )}
            </div>
            {fbMsg && (
              <div style={{
                fontSize: 13, fontWeight: 500, marginTop: 8,
                color: fbMsg.includes("Connected") || fbMsg.includes("disconnected") ? "#10b981" : "#e53935",
              }}>
                {fbMsg}
              </div>
            )}
          </div>
        </SettingsSection>
        )}

        {/* Branding */}
        <div ref={el => { sectionRefs.current.branding = el; }}>
        <SettingsSection title="Branding & Appearance">
          {FIELDS.filter((f) =>
            ["logo_url", "agent_profile_image", "agent_colour", "agent_font_colour", "agent_button_colour", "agent_font_url"].includes(f.key)
          ).map((f) =>
            f.type === "image" ? (
              <ImageField
                key={f.key}
                field={f}
                value={formValues[f.key]}
                uploading={uploadingField === f.key}
                onUpload={(file) => handleImageUpload(f.key, file)}
              />
            ) : f.type === "font" ? (
              <FontField
                key={f.key}
                field={f}
                value={formValues[f.key]}
                uploading={uploadingField === f.key}
                onUpload={(file) => handleFontUpload(f.key, file)}
              />
            ) : (
              <SettingsField
                key={f.key}
                field={f}
                value={formValues[f.key]}
                onChange={handleChange}
                isChanged={formValues[f.key] !== originalValues[f.key]}
              />
            )
          )}
          <SectionSaveBar
            sectionKey="branding"
            fieldKeys={["agent_colour", "agent_font_colour", "agent_button_colour"]}
          />
        </SettingsSection>
        </div>

        {/* Platform Config */}
        <div ref={el => { sectionRefs.current.platform_config = el; }}>
        <SettingsSection title="Platform Config">
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <FaPlug size={15} color="#0a9fa6" />
              <span style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>
                Property Report Delivery
              </span>
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 6px", lineHeight: 1.5 }}>
              Controls how visitors choose to receive their report on your{" "}
              <strong>Property Report landing page</strong> (the{" "}
              <code style={{ fontSize: 11, background: "#f1f5f9", padding: "1px 5px", borderRadius: 4 }}>/property-search</code>{" "}
              page).
            </p>
            <a
              href={propertySearchUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: 12, fontWeight: 600, color: "#1a3f6b",
                textDecoration: "none", marginBottom: 16,
              }}
            >
              <FaExternalLinkAlt size={10} /> Preview this page
            </a>

            {/* Layout choice */}
            <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
              Layout
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
              {PS_LAYOUTS.map((mode) => {
                const selected = psLayout === mode.key;
                return (
                  <button
                    key={mode.key}
                    type="button"
                    onClick={() => { setPsLayout(mode.key); setPlatformConfigMsg(""); }}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 12, textAlign: "left",
                      padding: "12px 14px", borderRadius: 10, cursor: "pointer",
                      border: selected ? "1.5px solid #10b981" : "1px solid #e5e7eb",
                      background: selected ? "#f0fdfa" : "#fff",
                      transition: "border-color 0.15s, background 0.15s",
                    }}
                  >
                    <span style={{
                      flexShrink: 0, marginTop: 2, width: 18, height: 18, borderRadius: "50%",
                      border: selected ? "5px solid #10b981" : "2px solid #d1d5db",
                      background: "#fff", transition: "border 0.15s",
                    }} />
                    <span style={{ flex: 1 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, fontSize: 13, color: "#111827" }}>
                        <span style={{ color: "#0a9fa6" }}>{mode.icon}</span> {mode.label}
                      </span>
                      <span style={{ display: "block", fontSize: 11.5, color: "#6b7280", lineHeight: 1.45, marginTop: 2 }}>
                        {mode.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Fields */}
            <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
              Fields to collect
            </div>
            <p style={{ fontSize: 11.5, color: "#6b7280", margin: "0 0 8px", lineHeight: 1.5 }}>
              The property address is always asked for. Enable any extra fields you'd like to capture
              and mark each as required or optional.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {PS_FIELD_DEFS.map((f) => {
                const state = psFields[f.key] || { enabled: false, required: false };
                return (
                  <div
                    key={f.key}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 14px", borderRadius: 10,
                      border: state.enabled ? "1.5px solid #10b981" : "1px solid #e5e7eb",
                      background: state.enabled ? "#f0fdfa" : "#fff",
                      transition: "border-color 0.15s, background 0.15s",
                    }}
                  >
                    <label style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, cursor: "pointer", margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={state.enabled}
                        onChange={(e) => togglePsField(f.key, {
                          enabled: e.target.checked,
                          required: e.target.checked ? state.required : false,
                        })}
                        style={{ width: 16, height: 16, cursor: "pointer" }}
                      />
                      <span style={{ color: "#0a9fa6", display: "flex", alignItems: "center" }}>{f.icon}</span>
                      <span style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>{f.label}</span>
                    </label>
                    <div style={{ display: "flex", gap: 4 }}>
                      {["optional", "required"].map((mode) => {
                        const isRequired = mode === "required";
                        const active = state.enabled && state.required === isRequired;
                        return (
                          <button
                            key={mode}
                            type="button"
                            disabled={!state.enabled}
                            onClick={() => togglePsField(f.key, { required: isRequired })}
                            style={{
                              padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                              border: active ? "1px solid #10b981" : "1px solid #e5e7eb",
                              background: active ? "#10b981" : "#fff",
                              color: active ? "#fff" : (state.enabled ? "#374151" : "#cbd5e1"),
                              cursor: state.enabled ? "pointer" : "not-allowed",
                              textTransform: "capitalize",
                            }}
                          >
                            {mode}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Property Report sections */}
            <div style={{ borderTop: "1px solid #eef2f7", marginTop: 22, paddingTop: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <FaListUl size={14} color="#0a9fa6" />
                <span style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>
                  Property Report Sections
                </span>
              </div>
              <p style={{ fontSize: 11.5, color: "#6b7280", margin: "0 0 10px", lineHeight: 1.5 }}>
                Choose which sections appear on the live{" "}
                <strong>Property Report</strong> (the{" "}
                <code style={{ fontSize: 11, background: "#f1f5f9", padding: "1px 5px", borderRadius: 4 }}>/property-profile</code>{" "}
                page). Disabled sections are hidden from viewers entirely.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {PR_SECTION_DEFS.map((s) => {
                  const enabled = reportSections[s.key] !== false;
                  return (
                    <label
                      key={s.key}
                      style={{
                        display: "flex", alignItems: "center", gap: 12, cursor: "pointer", margin: 0,
                        padding: "10px 14px", borderRadius: 10,
                        border: enabled ? "1.5px solid #10b981" : "1px solid #e5e7eb",
                        background: enabled ? "#f0fdfa" : "#fff",
                        transition: "border-color 0.15s, background 0.15s",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => toggleReportSection(s.key, e.target.checked)}
                        style={{ width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
                      />
                      <span style={{ flex: 1 }}>
                        <span style={{ display: "block", fontWeight: 600, fontSize: 13, color: "#111827" }}>{s.label}</span>
                        <span style={{ display: "block", fontSize: 11.5, color: "#6b7280", lineHeight: 1.4, marginTop: 1 }}>{s.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18 }}>
              <button
                onClick={handleSavePlatformConfig}
                disabled={!platformConfigHasChanges || !platformConfigValid || savingPlatformConfig}
                style={{
                  padding: "8px 20px", borderRadius: 8, border: "none",
                  background: (platformConfigHasChanges && platformConfigValid) ? "linear-gradient(90deg, #1a3f6b, #10b981)" : "#d1d5db",
                  color: "#fff", fontWeight: 600, fontSize: 14,
                  cursor: (platformConfigHasChanges && platformConfigValid) ? "pointer" : "default",
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {savingPlatformConfig ? <><InlineSpinner size={13} color="#fff" /> Saving...</> : <><FaSave size={11} /> Save Platform Config</>}
              </button>
              {platformConfigMsg && (
                <span style={{
                  fontSize: 13, fontWeight: 500,
                  color: platformConfigMsg.includes("success") ? "#10b981" : "#e53935",
                }}>
                  {platformConfigMsg}
                </span>
              )}
            </div>
          </div>
        </SettingsSection>
        </div>

        {/* Market Update Defaults */}
        <div ref={el => { sectionRefs.current.market = el; }}>
        <SettingsSection title="Market Update Defaults">
          {FIELDS.filter((f) =>
            ["default_market_update_state", "default_market_update_postcode", "default_market_update_suburb"].includes(f.key)
          ).map((f) => (
            <SettingsField
              key={f.key}
              field={f}
              value={formValues[f.key]}
              onChange={handleChange}
              isChanged={formValues[f.key] !== originalValues[f.key]}
            />
          ))}
          <SectionSaveBar
            sectionKey="market"
            fieldKeys={["default_market_update_state", "default_market_update_postcode", "default_market_update_suburb"]}
          />
        </SettingsSection>
        </div>

        {/* AI & Valuation */}
        <div ref={el => { sectionRefs.current.ai = el; }}>
        <SettingsSection title="AI & Valuation">
          {FIELDS.filter((f) =>
            ["global_context"].includes(f.key)
          ).map((f) => (
            <SettingsField
              key={f.key}
              field={f}
              value={formValues[f.key]}
              onChange={handleChange}
              isChanged={formValues[f.key] !== originalValues[f.key]}
            />
          ))}
          <SectionSaveBar
            sectionKey="ai"
            fieldKeys={["global_context"]}
          />
        </SettingsSection>
        </div>

        {/* AI Agent */}
        <div ref={el => { sectionRefs.current.ai_agent = el; }}>
        <SettingsSection title="AI Agent">
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <FaRobot size={16} color="#7c3aed" />
              <span style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>
                AI Agent Persona
              </span>
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.5 }}>
              Configure the AI persona that handles inbound conversations. It uses these details when drafting and (optionally) auto-sending replies.
            </p>

            {/* System Prompt (formerly Global Instructions) — owns the
                AI's full persona / voice / channel-style rules for every
                message it drafts (outbound follow-ups, inbound email
                replies and inbound SMS replies). Hoisted to the top of
                the AI Agent settings so it's clearly the primary control. */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 5, flexWrap: "wrap" }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                  System Prompt
                </label>
                <button
                  type="button"
                  onClick={handleRestoreDefaultSystemPrompt}
                  disabled={!defaultSystemPrompt || loadingDefaultPrompt}
                  title={defaultSystemPrompt ? "Replace the current system prompt with the default" : "Default prompt unavailable"}
                  style={{
                    padding: "5px 10px", borderRadius: 6,
                    border: "1px solid #d1d5db", background: "#fff",
                    color: !defaultSystemPrompt || loadingDefaultPrompt ? "#9ca3af" : "#374151",
                    fontSize: 12, fontWeight: 600,
                    cursor: !defaultSystemPrompt || loadingDefaultPrompt ? "not-allowed" : "pointer",
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}
                >
                  <FaUndo size={10} /> Restore Default
                </button>
              </div>
              <textarea
                value={aiGlobalInstructions}
                onChange={(e) => { setAiGlobalInstructions(e.target.value); setAiAgentSaveMsg(""); }}
                placeholder="The full instructions your AI follows when drafting messages."
                rows={20}
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: 8,
                  border: "1px solid #d1d5db", fontSize: 13,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                  boxSizing: "border-box", lineHeight: 1.5, resize: "vertical", minHeight: 320,
                }}
              />
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6, lineHeight: 1.5 }}>
                The complete system prompt your AI uses when drafting outbound
                follow-ups, inbound email replies and SMS replies. The placeholders{" "}
                <code>{"{ai_agent_name}"}</code>, <code>{"{ai_first_name}"}</code>,{" "}
                <code>{"{human_agent_name}"}</code> and <code>{"{agency_name}"}</code>{" "}
                are auto-filled at runtime. Use "Restore Default" to revert to the standard template.
              </div>
            </div>

            {/* Profile photo */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
                Profile Photo
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 72, height: 72, borderRadius: "50%",
                  background: aiAgentPhoto ? `url(${aiAgentPhoto}) center/cover` : "#f3f4f6",
                  border: "1px solid #e5e7eb", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {!aiAgentPhoto && <FaRobot size={26} color="#9ca3af" />}
                </div>
                <div>
                  <input
                    ref={aiPhotoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif"
                    onChange={(e) => handleAiPhotoUpload(e.target.files?.[0])}
                    style={{ display: "none" }}
                  />
                  <button
                    onClick={() => aiPhotoInputRef.current?.click()}
                    disabled={uploadingAiPhoto}
                    style={{
                      padding: "7px 14px", borderRadius: 8, border: "1px solid #d1d5db",
                      background: "#fff", color: "#374151", fontWeight: 600, fontSize: 13,
                      cursor: uploadingAiPhoto ? "not-allowed" : "pointer",
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                  >
                    {uploadingAiPhoto ? <InlineSpinner size={12} color="#374151" /> : <FaUpload size={11} />}
                    {aiAgentPhoto ? "Replace photo" : "Upload photo"}
                  </button>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>PNG, JPG or GIF. Square images work best.</div>
                </div>
              </div>
            </div>

            {/* AI agent name */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                Full Name
              </label>
              <input
                type="text"
                value={aiAgentName}
                onChange={(e) => { setAiAgentName(e.target.value); setAiAgentSaveMsg(""); }}
                placeholder="e.g. Alex Mitchell"
                style={{
                  width: "100%", padding: "8px 12px", borderRadius: 8,
                  border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box",
                }}
              />
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                Displayed as the From name on outbound AI replies.
              </div>
            </div>

            {/* AI agent first name */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                First Name
              </label>
              <input
                type="text"
                value={aiAgentFirstName}
                onChange={(e) => { setAiAgentFirstName(e.target.value); setAiAgentSaveMsg(""); }}
                placeholder="e.g. Alex"
                style={{
                  width: "100%", padding: "8px 12px", borderRadius: 8,
                  border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box",
                }}
              />
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                Used in personalised greetings within AI-generated emails.
              </div>
            </div>

            {/* AI agent title */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                Title
              </label>
              <input
                type="text"
                value={aiTitle}
                onChange={(e) => { setAiTitle(e.target.value); setAiAgentSaveMsg(""); }}
                placeholder={`Associate to ${agentDetails?.agent_name || "you"}`}
                style={{
                  width: "100%", padding: "8px 12px", borderRadius: 8,
                  border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box",
                }}
              />
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                Shown beneath the AI agent's name on outbound emails (e.g. signature). Defaults to "Associate to {agentDetails?.agent_name || "your name"}".
              </div>
            </div>

            {/* AI agent email prefix */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                Email Address
              </label>
              <div style={{ display: "flex", alignItems: "stretch", border: "1px solid #d1d5db", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
                <input
                  type="text"
                  value={aiAgentEmailPrefix}
                  onChange={(e) => {
                    const v = e.target.value.toLowerCase().replace(/[^a-z0-9._+-]/g, "");
                    setAiAgentEmailPrefix(v);
                    setAiAgentSaveMsg("");
                  }}
                  placeholder="alex"
                  style={{
                    flex: 1, minWidth: 0, padding: "8px 12px", border: "none",
                    fontSize: 14, outline: "none",
                  }}
                />
                <span style={{
                  display: "flex", alignItems: "center", padding: "0 12px",
                  background: "#f9fafb", borderLeft: "1px solid #e5e7eb",
                  color: "#6b7280", fontSize: 13, fontFamily: "monospace",
                }}>
                  {aiAgentDomain === "homeprice.site" && agentDetails?.agent_id
                    ? `.${agentDetails.agent_id}@${aiAgentDomain}`
                    : `@${aiAgentDomain}`}
                </span>
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                {aiAgentDomain === "homeprice.site" ? (
                  <>
                    No custom domain set — your AI will send from{" "}
                    <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 4 }}>
                      {(aiAgentEmailPrefix || "alex").trim().toLowerCase()}
                      {agentDetails?.agent_id ? `.${agentDetails.agent_id}` : ""}@homeprice.site
                    </code>
                    . Contact your account manager to set up a custom domain so your AI can send from your own brand.
                  </>
                ) : (
                  `Sending from your custom domain @${aiAgentDomain}.`
                )}
              </div>
            </div>

            {/* AI agent email signature */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                Email Signature
              </label>
              <textarea
                value={aiEmailSignature}
                onChange={(e) => { setAiEmailSignature(e.target.value); setAiAgentSaveMsg(""); }}
                rows={7}
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: 8,
                  border: "1px solid #d1d5db", fontSize: 13.5, fontFamily: "inherit",
                  boxSizing: "border-box", lineHeight: 1.5, resize: "vertical",
                }}
              />
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, lineHeight: 1.5 }}>
                Appended to every email the AI sends to your contacts. Available variables:{" "}
                <code>{"{{ai_agent_name}}"}</code>, <code>{"{{ai_agent_first_name}}"}</code>,{" "}
                <code>{"{{ai_title}}"}</code>, <code>{"{{agency_name}}"}</code>,{" "}
                <code>{"{{ai_agent_email}}"}</code>. Your AI agent's profile photo is shown automatically next to the signature.
              </div>

              {/* Signature logo (optional — falls back to the agency logo) */}
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
                  Signature Logo <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span>
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 120, height: 56, borderRadius: 8,
                    background: aiSignatureLogo ? `url(${aiSignatureLogo}) center/contain no-repeat` : "#f3f4f6",
                    border: "1px solid #e5e7eb", flexShrink: 0,
                  }} />
                  <div>
                    <input
                      ref={signatureLogoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif"
                      onChange={(e) => handleSignatureLogoUpload(e.target.files?.[0])}
                      style={{ display: "none" }}
                    />
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button
                        type="button"
                        onClick={() => signatureLogoInputRef.current?.click()}
                        disabled={uploadingSignatureLogo}
                        style={{
                          padding: "7px 14px", borderRadius: 8, border: "1px solid #d1d5db",
                          background: "#fff", color: "#374151", fontWeight: 600, fontSize: 13,
                          cursor: uploadingSignatureLogo ? "not-allowed" : "pointer",
                          display: "inline-flex", alignItems: "center", gap: 6,
                        }}
                      >
                        {uploadingSignatureLogo ? <InlineSpinner size={12} color="#374151" /> : <FaUpload size={11} />}
                        {aiSignatureLogo ? "Replace logo" : "Upload logo"}
                      </button>
                      {aiSignatureLogo && (
                        <button
                          type="button"
                          onClick={() => { setAiSignatureLogo(""); setAiAgentSaveMsg(""); }}
                          style={{
                            padding: "7px 12px", borderRadius: 8, border: "1px solid #d1d5db",
                            background: "#fff", color: "#b91c1c", fontWeight: 600, fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                      Shown at the bottom of the signature. If left empty, your agency logo is used.
                    </div>
                  </div>
                </div>
              </div>

              {/* Live preview of the rendered HTML signature */}
              {(() => {
                const agencyName = formValues.agency_name_long || "";
                const previewPrefix = (aiAgentEmailPrefix || "").trim().toLowerCase() || "ai";
                const previewSuffix = (aiAgentDomain === "homeprice.site" && agentDetails?.agent_id)
                  ? `.${agentDetails.agent_id}`
                  : "";
                const fullEmail = `${previewPrefix}${previewSuffix}@${aiAgentDomain}`;
                const rawBrand = (formValues.agent_colour || "").trim();
                const brand = (rawBrand && rawBrand.startsWith("#") && (rawBrand.length === 4 || rawBrand.length === 7))
                  ? rawBrand : "#0a9fa6";
                const agencyLogo = aiSignatureLogo || formValues.logo_url || "";
                const rendered = (aiEmailSignature || "")
                  .replace(/\{\{ai_agent_name\}\}/g, aiAgentName || "")
                  .replace(/\{\{ai_agent_first_name\}\}/g, aiAgentFirstName || "")
                  .replace(/\{\{ai_title\}\}/g, aiTitle || "")
                  .replace(/\{\{agency_name_long\}\}/g, agencyName)
                  .replace(/\{\{agency_name\}\}/g, agencyName)
                  .replace(/\{\{ai_agent_email\}\}/g, fullEmail);

                const nameNorm = (aiAgentName || "").trim().toLowerCase();
                const initial = (aiAgentName || "A").trim().charAt(0).toUpperCase();
                const lines = rendered.replace(/\r\n/g, "\n").split("\n").map(l => l.replace(/\s+$/, ""));
                let bolded = false;
                const renderedNodes = lines.map((line, i) => {
                  const stripped = line.trim();
                  if (!stripped) {
                    return <div key={i} style={{ height: 8 }}>&nbsp;</div>;
                  }
                  const isEmail = stripped.indexOf("@") !== -1 && stripped.indexOf(" ") === -1
                    && stripped.split("@").pop().indexOf(".") !== -1;
                  if (isEmail) {
                    return (
                      <div key={i} style={{ fontSize: 13, lineHeight: 1.5 }}>
                        <a href={`mailto:${stripped}`} style={{ color: "#374151", textDecoration: "none" }}>{stripped}</a>
                      </div>
                    );
                  }
                  if (!bolded && nameNorm && stripped.toLowerCase() === nameNorm) {
                    bolded = true;
                    return (
                      <div key={i} style={{ fontWeight: 700, color: "#111827", fontSize: 15, lineHeight: 1.4 }}>{stripped}</div>
                    );
                  }
                  return (
                    <div key={i} style={{ color: "#374151", fontSize: 13, lineHeight: 1.5 }}>{stripped}</div>
                  );
                });

                return (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                      Preview
                    </div>
                    <div style={{
                      border: "1px solid #e5e7eb", borderRadius: 10, background: "#ffffff",
                      padding: "18px 20px", fontFamily: "Arial, Helvetica, sans-serif", color: "#111827",
                    }}>
                      <div style={{ fontSize: 14, lineHeight: 1.6, color: "#9ca3af", fontStyle: "italic", marginBottom: 18 }}>
                        [Your AI's reply to the contact appears here]
                      </div>
                      <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16, display: "flex", alignItems: "flex-start", gap: 14 }}>
                        {aiAgentPhoto ? (
                          <img
                            src={aiAgentPhoto}
                            alt={aiAgentName || ""}
                            style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: "1px solid #e5e7eb", flexShrink: 0 }}
                          />
                        ) : (
                          <div style={{
                            width: 64, height: 64, borderRadius: "50%", background: brand,
                            color: "#fff", fontSize: 26, fontWeight: 700, lineHeight: "64px",
                            textAlign: "center", flexShrink: 0,
                          }}>
                            {initial}
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {renderedNodes}
                          {agencyLogo && (
                            <div style={{ marginTop: 12 }}>
                              <img
                                src={agencyLogo}
                                alt={agencyName}
                                style={{ display: "block", maxHeight: 40, maxWidth: 200, width: "auto", height: "auto" }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Send a sample email so the agent can preview the AI's emails in their own inbox */}
            <div style={{
              marginBottom: 14, padding: "12px 14px", borderRadius: 10,
              border: "1px solid #e5e7eb", background: "#f9fafb",
            }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                Send a sample email
              </label>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8, lineHeight: 1.5 }}>
                Sends a generic introduction from your AI agent to the address below so you can preview
                the layout, signature and sender exactly as your contacts will see it.
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
                <input
                  type="email"
                  value={testEmailTo}
                  onChange={(e) => { setTestEmailTo(e.target.value); setTestEmailEdited(true); setTestEmailMsg(""); }}
                  placeholder="recipient@example.com"
                  style={{
                    flex: "1 1 220px", minWidth: 0, padding: "9px 12px",
                    borderRadius: 8, border: "1px solid #d1d5db",
                    fontSize: 13.5, fontFamily: "inherit", boxSizing: "border-box",
                  }}
                />
                <button
                  type="button"
                  onClick={handleSendTestEmail}
                  disabled={sendingTestEmail || !testEmailTo.trim()}
                  style={{
                    padding: "9px 16px", borderRadius: 8, border: "none",
                    background: sendingTestEmail || !testEmailTo.trim() ? "#9ca3af" : "#7c3aed",
                    color: "#fff", fontSize: 13, fontWeight: 600,
                    cursor: sendingTestEmail || !testEmailTo.trim() ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {sendingTestEmail ? "Sending…" : "Send test email"}
                </button>
              </div>
              {testEmailMsg && (
                <div style={{
                  marginTop: 8, fontSize: 12,
                  color: testEmailMsg.startsWith("Sent to") ? "#047857" : "#b91c1c",
                }}>
                  {testEmailMsg}
                </div>
              )}
            </div>

            {/* Outbound AI follow-up + warm-up campaigns mode.
                Three-way switch:
                  Off    — skip this agent's contacts entirely.
                  On     — send drafted emails directly.
                  Review — stage drafted emails in AI Conversations → Needs
                           Review for the agent to approve, edit, or dismiss. */}
            <div style={{
              padding: "12px 14px", borderRadius: 10,
              border: "1px solid #e5e7eb",
              background: aiOutboundMode === 'on' ? "#ecfdf5"
                       : aiOutboundMode === 'review' ? "#f5f3ff"
                       : "#f9fafb",
              marginBottom: 10,
            }}>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", marginBottom: 2 }}>
                  {aiOutboundMode === 'on'
                    ? "Outbound AI follow-up & warm-up campaigns: ON"
                    : aiOutboundMode === 'review'
                    ? "Outbound AI follow-up & warm-up campaigns: REVIEW"
                    : "Outbound AI follow-up & warm-up campaigns: OFF"}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.4 }}>
                  {aiOutboundMode === 'on'
                    ? "The AI will email your contacts on your behalf — property update warm-ups, market update warm-ups and incomplete-analysis nudges."
                    : aiOutboundMode === 'review'
                    ? "Drafted outbound emails appear in AI Conversations → Needs Review for you to approve, edit, or dismiss before they send. Nothing goes out without your sign-off."
                    : "No outbound follow-up or warm-up emails will be sent to your contacts on your behalf."}
                </div>
              </div>
              <div
                role="radiogroup"
                aria-label="Outbound AI follow-up mode"
                style={{
                  display: "inline-flex", padding: 3, borderRadius: 8,
                  background: "#e5e7eb", gap: 0,
                }}
              >
                {[
                  { key: 'off', label: 'Off', activeBg: '#9ca3af' },
                  { key: 'on', label: 'On', activeBg: '#10b981' },
                  { key: 'review', label: 'Review', activeBg: '#7c3aed' },
                ].map(opt => {
                  const active = aiOutboundMode === opt.key;
                  return (
                    <button
                      key={opt.key}
                      role="radio"
                      aria-checked={active}
                      onClick={() => { setAiOutboundMode(opt.key); setAiAgentSaveMsg(""); }}
                      style={{
                        padding: "5px 14px", borderRadius: 6, border: "none",
                        background: active ? opt.activeBg : "transparent",
                        color: active ? "#fff" : "#374151",
                        fontSize: 12, fontWeight: 600, cursor: "pointer",
                        transition: "background 0.15s, color 0.15s",
                        minWidth: 56,
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Auto-reply toggle */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "12px 14px", borderRadius: 10,
              border: "1px solid #e5e7eb", background: aiAutoReply ? "#fef3c7" : "#f0fdfa",
              marginBottom: 14,
            }}>
              <div style={{ flex: 1, marginRight: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>
                  {aiAutoReply ? "Auto-reply enabled" : "Review all responses"}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.4 }}>
                  {aiAutoReply
                    ? "The AI will send replies directly to contacts without your approval."
                    : "All AI-generated replies are sent to you first for review and approval."}
                </div>
              </div>
              <button
                onClick={() => { setAiAutoReply(prev => !prev); setAiAgentSaveMsg(""); }}
                style={{
                  width: 44, height: 24, borderRadius: 12, border: "none",
                  background: aiAutoReply ? "#f59e0b" : "#10b981",
                  cursor: "pointer", position: "relative", transition: "background 0.2s",
                  flexShrink: 0,
                }}
              >
                <span style={{
                  position: "absolute", top: 2, left: aiAutoReply ? 22 : 2,
                  width: 20, height: 20, borderRadius: "50%", background: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s",
                }} />
              </button>
            </div>

            {/* Global Instructions / System Prompt — moved to the top of this section. */}

            {/* Save button */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
              <button
                onClick={handleSaveAiAgent}
                disabled={!aiAgentHasChanges || savingAiAgent}
                style={{
                  padding: "8px 20px", borderRadius: 8, border: "none",
                  background: aiAgentHasChanges ? "linear-gradient(90deg, #1a3f6b, #7c3aed)" : "#d1d5db",
                  color: "#fff", fontWeight: 600, fontSize: 14,
                  cursor: aiAgentHasChanges ? "pointer" : "default",
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {savingAiAgent ? <><InlineSpinner size={13} color="#fff" /> Saving...</> : <><FaSave size={11} /> Save AI Agent</>}
              </button>
              {aiAgentSaveMsg && (
                <span style={{
                  fontSize: 13, fontWeight: 500,
                  color: aiAgentSaveMsg.includes("success") ? "#10b981" : "#e53935",
                }}>
                  {aiAgentSaveMsg}
                </span>
              )}
            </div>

            {/* Import Email History card */}
            <div style={{
              marginTop: 24, padding: 16, borderRadius: 10,
              background: "#f0fdfa", border: "1px solid #ccfbf1",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <FaEnvelopeOpenText size={15} color="#0f766e" />
                <span style={{ fontWeight: 600, fontSize: 14, color: "#134e4a" }}>
                  Give your AI memory of past conversations
                </span>
              </div>
              <p style={{ fontSize: 13, color: "#134e4a", lineHeight: 1.55, margin: "0 0 12px" }}>
Upload your inbox export and we'll pull out a short summary of where each relationship is up to — things like "spoke about listing in March" or "prefers to deal with the husband on finances" — and save it onto the contact's profile. Without it, your AI starts every conversation from scratch and misses the context you already have. Your emails stay private to your account; only the per-contact summary is used to brief the AI.
              </p>
              <button
                onClick={() => setShowMboxModal(true)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "8px 16px", borderRadius: 8,
                  background: "#0f766e", color: "#fff",
                  border: "none", fontSize: 13, fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <FaEnvelopeOpenText size={12} /> Import Email History
              </button>
            </div>
          </div>
        </SettingsSection>
        </div>

        {/* Target Suburbs */}
        <div ref={el => { sectionRefs.current.suburbs = el; }}>
        <SettingsSection title="Target Suburbs">
          <TargetSuburbsField
            targetSuburbs={targetSuburbs}
            suburbQuery={suburbQuery}
            setSuburbQuery={setSuburbQuery}
            suburbResults={suburbResults}
            suburbSearching={suburbSearching}
            suburbDropdownOpen={suburbDropdownOpen}
            suburbDropdownRef={suburbDropdownRef}
            suburbInputRef={suburbInputRef}
            searchSuburbs={searchSuburbs}
            setSuburbDropdownOpen={setSuburbDropdownOpen}
            handleSelectSuburb={handleSelectSuburb}
            handleRemoveSuburb={handleRemoveSuburb}
            handleSaveSuburbs={handleSaveSuburbs}
            savingSuburbs={savingSuburbs}
            suburbIdsChanged={suburbIdsChanged}
            suburbSaveMsg={suburbSaveMsg}
          />
        </SettingsSection>
        </div>

        {/* CRM Integrations */}
        <div ref={el => { sectionRefs.current.integrations = el; }}>
        <SettingsSection title="CRM Integrations">
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <FaPlug size={16} color="#0f766e" />
              <span style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>
                Connected CRMs
              </span>
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.5 }}>
              Connect your CRM to let our AI agent securely access and sync your contacts. You can add multiple integrations.
            </p>

            {crmLoading ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <Spinner animation="border" size="sm" style={{ color: "#0f766e" }} />
              </div>
            ) : crmIntegrations.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                {crmIntegrations.map((intg) => {
                  const typeLabels = {
                    agentbox: "AgentBox", eagle: "Eagle", lockedon: "LockedOn",
                    vaultre: "VaultRE", boxdice: "Box+Dice", rex: "Rex",
                    pipedrive: "Pipedrive", other: "Other",
                  };
                  return (
                    <div key={intg.id} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "12px 14px", borderRadius: 10,
                      border: "1px solid #e5e7eb", background: "#fafafa",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: "linear-gradient(135deg, #0f766e 0%, #10b981 100%)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          <FaPlug size={12} color="#fff" />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>
                            {intg.label || typeLabels[intg.crm_type] || intg.crm_type}
                          </div>
                          <div style={{ fontSize: 11, color: "#6b7280" }}>
                            {typeLabels[intg.crm_type] || intg.crm_type}
                            {intg.other_crm_name ? ` — ${intg.other_crm_name}` : ""}
                            {intg.login_url ? ` · ${intg.login_url}` : ""}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteIntegration(intg.id)}
                        disabled={crmDeleting === intg.id}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          padding: 6, color: "#9ca3af", transition: "color 0.15s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "#dc2626"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; }}
                        title="Remove integration"
                      >
                        {crmDeleting === intg.id
                          ? <InlineSpinner size={13} color="#dc2626" />
                          : <FaTrash size={12} />
                        }
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                textAlign: "center", padding: "20px 0", color: "#9ca3af",
                fontSize: 13, marginBottom: 14,
              }}>
                No CRM integrations yet.
              </div>
            )}

            <button
              onClick={() => setShowCRMModal(true)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 8, width: "100%", padding: "10px 0", borderRadius: 10,
                border: "1.5px dashed #0f766e", background: "#fff",
                color: "#0f766e", fontSize: 14, fontWeight: 600,
                cursor: "pointer", transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdfa"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
            >
              <FaPlus size={11} /> Add CRM Integration
            </button>
          </div>
        </SettingsSection>
        </div>
        <ConnectCRMModal
          show={showCRMModal}
          onClose={() => setShowCRMModal(false)}
          onSuccess={() => {
            loadCrmIntegrations();
          }}
        />
        <UploadMboxModal
          show={showMboxModal}
          onClose={() => setShowMboxModal(false)}
        />
        {showBuyCredit && (
          <BuyExtraUsageModal
            onClose={() => setShowBuyCredit(false)}
            defaultAmount={25}
            returnUrl={`${window.location.origin}/settings`}
          />
        )}

        {/* Notifications */}
        <div ref={el => { sectionRefs.current.notifications = el; }}>
        <SettingsSection title="Notification Preferences">
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <FaBell size={16} color="#0a9fa6" />
              <span style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>
                Live Email Notifications
              </span>
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.5 }}>
              Control which events trigger a live email notification to you. These are real-time alerts sent when users interact with your branded page.
            </p>

            {/* Master toggle */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "12px 14px", borderRadius: 10,
              border: "1px solid #e5e7eb", background: notifEnabled ? "#f0fdfa" : "#fafafa",
              marginBottom: 16,
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>
                  {notifEnabled ? "Notifications Enabled" : "Notifications Disabled"}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>
                  {notifEnabled ? "You will receive email alerts for the event types selected below." : "All live email notifications are turned off."}
                </div>
              </div>
              <button
                onClick={() => { setNotifEnabled(prev => !prev); setNotifSaveMsg(""); }}
                style={{
                  width: 44, height: 24, borderRadius: 12, border: "none",
                  background: notifEnabled ? "#10b981" : "#d1d5db",
                  cursor: "pointer", position: "relative", transition: "background 0.2s",
                  flexShrink: 0,
                }}
              >
                <span style={{
                  position: "absolute", top: 2, left: notifEnabled ? 22 : 2,
                  width: 20, height: 20, borderRadius: "50%", background: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s",
                }} />
              </button>
            </div>

            {/* Per-event toggles */}
            {notifEnabled && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {NOTIFICATION_EVENT_TYPES.map(evt => (
                  <div key={evt.key} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", borderRadius: 8,
                    border: "1px solid #f0f0f0", background: "#fff",
                  }}>
                    <div style={{ flex: 1, marginRight: 12 }}>
                      <div style={{ fontWeight: 500, fontSize: 13, color: "#1f2937" }}>{evt.label}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af", lineHeight: 1.4 }}>{evt.description}</div>
                    </div>
                    <button
                      onClick={() => {
                        setNotifEventTypes(prev => ({ ...prev, [evt.key]: !prev[evt.key] }));
                        setNotifSaveMsg("");
                      }}
                      style={{
                        width: 38, height: 20, borderRadius: 10, border: "none",
                        background: notifEventTypes[evt.key] ? "#10b981" : "#d1d5db",
                        cursor: "pointer", position: "relative", transition: "background 0.2s",
                        flexShrink: 0,
                      }}
                    >
                      <span style={{
                        position: "absolute", top: 2, left: notifEventTypes[evt.key] ? 20 : 2,
                        width: 16, height: 16, borderRadius: "50%", background: "#fff",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.2)", transition: "left 0.2s",
                      }} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Save button */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
              <button
                onClick={handleSaveNotifSettings}
                disabled={!notifHasChanges || savingNotif}
                style={{
                  padding: "8px 20px", borderRadius: 8, border: "none",
                  background: notifHasChanges ? "linear-gradient(90deg, #1a3f6b, #10b981)" : "#d1d5db",
                  color: "#fff", fontWeight: 600, fontSize: 14,
                  cursor: notifHasChanges ? "pointer" : "default",
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {savingNotif ? <><InlineSpinner size={13} color="#fff" /> Saving...</> : <><FaSave size={11} /> Save Notifications</>}
              </button>
              {notifSaveMsg && (
                <span style={{
                  fontSize: 13, fontWeight: 500,
                  color: notifSaveMsg.includes("success") ? "#10b981" : "#e53935",
                }}>
                  {notifSaveMsg}
                </span>
              )}
            </div>
          </div>
        </SettingsSection>
        </div>

        {/* Outbound Campaigns */}
        <div ref={el => { sectionRefs.current.campaigns = el; }}>
        <SettingsSection title="Outbound Campaigns">
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <FaEnvelopeOpenText size={16} color="#0a9fa6" />
              <span style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>
                Marketing Communications
              </span>
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.5 }}>
              Control which automated outbound campaigns send to your contacts on your behalf.
            </p>

            {/* Per-campaign sections. Order is fixed: Property Email,
                Suburb Email, Property SMS, Suburb SMS. Each section bundles
                the on/off toggle, the daily limit (SMS), a sample-send row,
                and the editable SMS templates (SMS). */}
            {[
              {
                key: "property_update_email",
                title: "Property Update Email",
                description: "Property report emails sent to your contacts with the latest estimate for the property they're tracking.",
                isSms: false,
                sample: {
                  type: "email",
                  placeholder: "name@example.com",
                  value: samplePropertyEmail,
                  setValue: setSamplePropertyEmail,
                  setMsg: setSamplePropertyMsg,
                  sending: samplePropertySending,
                  msg: samplePropertyMsg,
                  onSend: handleSendPropertySample,
                },
              },
              {
                key: "price_update_email",
                title: "Suburb Update Email",
                description: "Market update emails sent to your contacts when their suburb's median price changes.",
                isSms: false,
                sample: {
                  type: "email",
                  placeholder: "name@example.com",
                  value: sampleMarketEmail,
                  setValue: setSampleMarketEmail,
                  setMsg: setSampleMarketMsg,
                  sending: sampleMarketSending,
                  msg: sampleMarketMsg,
                  onSend: handleSendMarketSample,
                },
              },
              {
                key: "property_update_sms",
                title: "Property Update SMS",
                description: "SMS sent alongside the property update email (where a mobile is available), linking contacts to their latest estimate.",
                isSms: true,
                sample: {
                  type: "tel",
                  placeholder: "0421 346 175",
                  value: samplePropertySms,
                  setValue: setSamplePropertySms,
                  setMsg: setSamplePropertySmsMsg,
                  sending: samplePropertySmsSending,
                  msg: samplePropertySmsMsg,
                  onSend: handleSendPropertySmsSample,
                },
              },
              {
                key: "new_data_available_sms",
                title: "Suburb Update SMS",
                description: "SMS sent to mobile-only contacts when new property data is available, linking them to your branded valuation page.",
                isSms: true,
                sample: {
                  type: "tel",
                  placeholder: "0421 346 175",
                  value: sampleMarketSms,
                  setValue: setSampleMarketSms,
                  setMsg: setSampleMarketSmsMsg,
                  sending: sampleMarketSmsSending,
                  msg: sampleMarketSmsMsg,
                  onSend: handleSendMarketSmsSample,
                },
              },
            ].map((section) => {
              const enabled = jobSettings[section.key];
              const s = section.sample;
              // SMS credit balance sits between the email sections and the SMS
              // sections — it only applies to SMS sends, so it introduces them.
              const showCreditBanner = section.key === "property_update_sms";
              return (
                <React.Fragment key={section.key}>
                  {showCreditBanner && (
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "10px 14px", borderRadius: 8,
                      border: "1px solid #e0f2fe", background: "#f0f9ff",
                      marginBottom: 10,
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#0c4a6e" }}>SMS Credit Balance</div>
                        <div style={{ fontSize: 11, color: "#0369a1", lineHeight: 1.4 }}>
                          Each SMS message part costs 20¢.
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: balanceCents > 0 ? "#059669" : "#dc2626" }}>
                          ${(balanceCents / 100).toFixed(2)}
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowBuyCredit(true)}
                          style={{
                            background: "#0a9fa6", color: "#fff", border: "none",
                            borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Top up
                        </button>
                      </div>
                    </div>
                  )}
                <div style={{
                  marginBottom: 14, padding: "14px 16px", borderRadius: 10,
                  border: "1px solid #e5e7eb", background: "#fff",
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}>{section.title}</div>
                      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.45, marginTop: 2 }}>
                        {section.description}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                      {section.isSms && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <label style={{ fontSize: 11, color: "#6b7280" }}>Daily limit</label>
                          <input
                            type="number"
                            min={0}
                            max={500}
                            value={jobDailyLimits[section.key] ?? SMS_DAILY_LIMIT_DEFAULT}
                            onChange={e => {
                              const raw = e.target.value;
                              const n = raw === "" ? 0 : parseInt(raw, 10);
                              setJobDailyLimits(prev => ({
                                ...prev,
                                [section.key]: Number.isFinite(n) && n >= 0 ? n : 0,
                              }));
                              setJobsSaveMsg("");
                            }}
                            style={{
                              width: 60, padding: "4px 6px", fontSize: 12,
                              borderRadius: 6, border: "1px solid #d1d5db",
                              textAlign: "right",
                            }}
                          />
                          {(() => {
                            const parts = smsFollowUpParts[section.key];
                            const limit = jobDailyLimits[section.key] ?? SMS_DAILY_LIMIT_DEFAULT;
                            if (!parts) return null;
                            const cost = (limit * parts * 20) / 100;
                            return (
                              <span
                                title={`Max daily cost = daily limit (${limit}) × follow-up parts (${parts}) × 20¢`}
                                style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}
                              >
                                ≈ ${cost.toFixed(2)}/day
                              </span>
                            );
                          })()}
                        </div>
                      )}
                      <button
                        onClick={() => {
                          // Block turning a campaign ON when the agent doesn't
                          // have an active (non-trialing) paid subscription.
                          // Turning OFF is always allowed.
                          if (!enabled && !isSubscriptionActive) {
                            setShowBillingRequiredModal(true);
                            return;
                          }
                          if (!enabled && section.isSms && balanceCents <= 0) {
                            setShowSmsCreditRequiredModal(true);
                            return;
                          }
                          setJobSettings(prev => ({ ...prev, [section.key]: !prev[section.key] }));
                          setJobsSaveMsg("");
                        }}
                        style={{
                          width: 38, height: 20, borderRadius: 10, border: "none",
                          background: enabled ? "#10b981" : "#d1d5db",
                          cursor: "pointer", position: "relative", transition: "background 0.2s",
                          flexShrink: 0,
                        }}
                        aria-label={`Toggle ${section.title}`}
                      >
                        <span style={{
                          position: "absolute", top: 2, left: enabled ? 20 : 2,
                          width: 16, height: 16, borderRadius: "50%", background: "#fff",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.2)", transition: "left 0.2s",
                        }} />
                      </button>
                    </div>
                  </div>

                  {section.isSms && (
                    <div style={{ marginTop: 4, marginBottom: 12, paddingTop: 12, borderTop: "1px dashed #f0f0f0" }}>
                      <CampaignSmsTemplatesEditor
                        campaignKey={section.key}
                        agencyNameShort={agentDetails?.agency_name_short || agentDetails?.agency_name_long || agentDetails?.agency_name}
                        agentName={agentDetails?.agent_name}
                        onFollowUpPartsChange={(parts) =>
                          setSmsFollowUpParts(prev =>
                            prev[section.key] === parts ? prev : { ...prev, [section.key]: parts }
                          )
                        }
                      />
                    </div>
                  )}

                  <div style={{ marginTop: 4, paddingTop: 12, borderTop: "1px dashed #f0f0f0" }}>
                    <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                      Send a sample
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <input
                        type={s.type}
                        value={s.value}
                        placeholder={s.placeholder}
                        onChange={(e) => { s.setValue(e.target.value); s.setMsg(""); }}
                        onKeyDown={(e) => { if (e.key === "Enter" && !s.sending) s.onSend(); }}
                        style={{
                          flex: 1, minWidth: 200, padding: "8px 12px",
                          borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14,
                          boxSizing: "border-box",
                        }}
                      />
                      <button
                        onClick={s.onSend}
                        disabled={s.sending || !s.value.trim()}
                        style={{
                          padding: "8px 18px", borderRadius: 8, border: "none",
                          background: (!s.sending && s.value.trim()) ? "linear-gradient(90deg, #1a3f6b, #10b981)" : "#d1d5db",
                          color: "#fff", fontWeight: 600, fontSize: 14,
                          cursor: (!s.sending && s.value.trim()) ? "pointer" : "default",
                          display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                          minWidth: 130, justifyContent: "center",
                        }}
                      >
                        {s.sending ? <><InlineSpinner size={13} color="#fff" /> Sending...</> : "Send Sample"}
                      </button>
                    </div>
                    {s.msg && (
                      <div style={{
                        fontSize: 12, fontWeight: 500, marginTop: 5,
                        color: s.msg.includes("sent to") ? "#10b981" : "#e53935",
                      }}>
                        {s.msg}
                      </div>
                    )}
                  </div>
                </div>
                </React.Fragment>
              );
            })}

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
              <button
                onClick={handleSaveJobSettings}
                disabled={!jobsHaveChanges || savingJobs}
                style={{
                  padding: "8px 20px", borderRadius: 8, border: "none",
                  background: jobsHaveChanges ? "linear-gradient(90deg, #1a3f6b, #10b981)" : "#d1d5db",
                  color: "#fff", fontWeight: 600, fontSize: 14,
                  cursor: jobsHaveChanges ? "pointer" : "default",
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {savingJobs ? <><InlineSpinner size={13} color="#fff" /> Saving...</> : <><FaSave size={11} /> Save Campaigns</>}
              </button>
              {jobsSaveMsg && (
                <span style={{
                  fontSize: 13, fontWeight: 500,
                  color: jobsSaveMsg.includes("success") ? "#10b981" : "#e53935",
                }}>
                  {jobsSaveMsg}
                </span>
              )}
            </div>
          </div>
        </SettingsSection>
        </div>

        {/* Contact Tags */}
        <div ref={el => { sectionRefs.current.tags = el; }}>
          <ContactTagsSettingsSection />
        </div>

        {/* Referral — only for agents flagged active in agent_details */}
        {agentDetails?.active && (
          <div ref={el => { sectionRefs.current.referral = el; }}>
            <ReferralSettingsSection />
          </div>
        )}

        {/* Security */}
        <div ref={el => { sectionRefs.current.security = el; }}>
        <SettingsSection title="Security">
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Change Password</label>
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 12px", lineHeight: 1.5 }}>
              Set or update the password used to log in to the platform.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <input
                type="password"
                placeholder="New password (min 8 chars)"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPasswordMsg(''); }}
                style={{
                  flex: 1,
                  minWidth: 180,
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
              <input
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPasswordMsg(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSavePassword(); }}
                style={{
                  flex: 1,
                  minWidth: 180,
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
              <button
                onClick={handleSavePassword}
                disabled={passwordLoading || !newPassword}
                style={{
                  padding: "8px 20px",
                  borderRadius: 8,
                  border: "none",
                  background: newPassword ? "linear-gradient(90deg, #1a3f6b, #10b981)" : "#d1d5db",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: newPassword ? "pointer" : "default",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                }}
              >
                {passwordLoading ? <><InlineSpinner size={13} color="#fff" /> Saving...</> : <><FaLock size={11} /> Update Password</>}
              </button>
            </div>
            {passwordMsg && (
              <div style={{
                fontSize: 13,
                fontWeight: 500,
                color: passwordMsg.includes('success') ? '#10b981' : '#e53935',
                marginTop: 8,
              }}>
                {passwordMsg}
              </div>
            )}
          </div>
        </SettingsSection>
        </div>

        {/* Log Out */}
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            marginBottom: 24,
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, color: "#1a1a1a", marginBottom: 2 }}>Log Out</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Sign out of your VendorLeads account on this device.</div>
          </div>
          <button
            onClick={() => {
              localStorage.removeItem("jwt_token");
              localStorage.removeItem("user");
              navigate("/", { replace: true });
            }}
            style={{
              padding: "8px 20px",
              borderRadius: 8,
              border: "1px solid #fecaca",
              background: "#fff",
              color: "#dc2626",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
              transition: "background 0.15s",
            }}
          >
            <FaSignOutAlt size={13} /> Log Out
          </button>
        </div>

        {/* Save bar */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 24,
            justifyContent: "flex-end",
          }}
        >
          {saveMsg && (
            <span
              style={{
                fontSize: 13,
                color: saveMsg.includes("success") ? "#10b981" : "#e53935",
                marginRight: "auto",
                fontWeight: 500,
              }}
            >
              {saveMsg}
            </span>
          )}
          <button
            onClick={handleReset}
            disabled={!hasChanges || saving}
            style={{
              padding: "8px 20px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              background: "#fff",
              color: hasChanges ? "#374151" : "#aaa",
              fontWeight: 600,
              fontSize: 14,
              cursor: hasChanges ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              gap: 6,
              transition: "background 0.15s",
            }}
          >
            <FaUndo size={11} /> Reset
          </button>
          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={!hasChanges || saving}
            style={{
              padding: "8px 24px",
              borderRadius: 8,
              border: "none",
              background: hasChanges
                ? "linear-gradient(90deg, #1a3f6b, #10b981)"
                : "#d1d5db",
              color: "#fff",
              fontWeight: 600,
              fontSize: 14,
              cursor: hasChanges ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              gap: 6,
              transition: "background 0.15s",
            }}
          >
            {saving ? <><InlineSpinner size={13} color="#fff" /> Saving...</> : <><FaSave size={11} /> Save Changes</>}
          </button>
        </div>
      </div>

      {/* Billing required modal — shown when an agent without an active paid
          subscription tries to enable an outbound campaign. */}
      {showBillingRequiredModal && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
            background: "rgba(0,0,0,0.35)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowBillingRequiredModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 12,
              boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
              width: "100%", maxWidth: 460, margin: "0 16px", overflow: "hidden",
            }}
          >
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "16px 20px", borderBottom: "1px solid #e5e7eb",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FaLock style={{ color: "#0a9fa6", fontSize: 16 }} />
                <span style={{ fontWeight: 600, fontSize: 16, color: "#1f2937" }}>
                  Payment Required
                </span>
              </div>
              <button
                onClick={() => setShowBillingRequiredModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" }}
              >
                <FaTimes style={{ fontSize: 16, color: "#9ca3af" }} />
              </button>
            </div>
            <div style={{ padding: "18px 20px" }}>
              <button
                onClick={() => {
                  setShowBillingRequiredModal(false);
                  navigate("/billing");
                }}
                style={{
                  width: "100%", background: "#0a9fa6", color: "#fff",
                  border: "none", borderRadius: 8, padding: "10px 14px",
                  fontWeight: 600, fontSize: 14, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  marginBottom: 14,
                }}
              >
                <FaExternalLinkAlt size={11} /> Go to Billing
              </button>
              <p style={{ fontSize: 14, color: "#4b5563", margin: 0, lineHeight: 1.5 }}>
                To turn on outbound campaigns your account needs an active subscription
                with a payment method on file. Head to billing to add a credit card or
                set up direct debit, then come back and flip the campaign on.
              </p>
            </div>
            <div style={{
              padding: "12px 20px", borderTop: "1px solid #e5e7eb",
              display: "flex", justifyContent: "flex-end", gap: 8,
            }}>
              <button
                onClick={() => setShowBillingRequiredModal(false)}
                style={{
                  background: "#fff", color: "#374151",
                  border: "1px solid #d1d5db", borderRadius: 8,
                  padding: "8px 14px", fontWeight: 500, fontSize: 13, cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMS credit required modal — shown when an agent with $0 SMS credit
          tries to enable an SMS campaign. */}
      {showSmsCreditRequiredModal && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
            background: "rgba(0,0,0,0.35)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowSmsCreditRequiredModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 12,
              boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
              width: "100%", maxWidth: 460, margin: "0 16px", overflow: "hidden",
            }}
          >
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "16px 20px", borderBottom: "1px solid #e5e7eb",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FaMobileAlt style={{ color: "#0a9fa6", fontSize: 16 }} />
                <span style={{ fontWeight: 600, fontSize: 16, color: "#1f2937" }}>
                  SMS Credit Required
                </span>
              </div>
              <button
                onClick={() => setShowSmsCreditRequiredModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" }}
              >
                <FaTimes style={{ fontSize: 16, color: "#9ca3af" }} />
              </button>
            </div>
            <div style={{ padding: "18px 20px" }}>
              <button
                onClick={() => {
                  setShowSmsCreditRequiredModal(false);
                  setShowBuyCredit(true);
                }}
                style={{
                  width: "100%", background: "#0a9fa6", color: "#fff",
                  border: "none", borderRadius: 8, padding: "10px 14px",
                  fontWeight: 600, fontSize: 14, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  marginBottom: 14,
                }}
              >
                <FaPlus size={11} /> Top up SMS credit
              </button>
              <p style={{ fontSize: 14, color: "#4b5563", margin: 0, lineHeight: 1.5 }}>
                Your SMS credit balance is $0. SMS campaigns cost 20¢ per message part,
                so you'll need to top up before you can turn them on.
              </p>
            </div>
            <div style={{
              padding: "12px 20px", borderTop: "1px solid #e5e7eb",
              display: "flex", justifyContent: "flex-end", gap: 8,
            }}>
              <button
                onClick={() => setShowSmsCreditRequiredModal(false)}
                style={{
                  background: "#fff", color: "#374151",
                  border: "1px solid #d1d5db", borderRadius: 8,
                  padding: "8px 14px", fontWeight: 500, fontSize: 13, cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.35)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
              width: "100%",
              maxWidth: 480,
              margin: "0 16px",
              overflow: "hidden",
            }}
          >
            {/* Modal header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 20px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FaExclamationTriangle style={{ color: "#f59e0b", fontSize: 16 }} />
                <span style={{ fontWeight: 600, fontSize: 16, color: "#1f2937" }}>Confirm Changes</span>
              </div>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FaTimes style={{ fontSize: 16, color: "#9ca3af" }} />
              </button>
            </div>

            {/* Modal body */}
            <div style={{ padding: "16px 20px" }}>
              <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 14px" }}>
                You are about to update the following settings. These changes will be reflected across your landing page, emails, and reports.
              </p>
              <div
                style={{
                  background: "#f9fafb",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  maxHeight: 260,
                  overflowY: "auto",
                }}
              >
                {getChangedFields().map((f, i) => (
                  <div
                    key={f.key}
                    style={{
                      padding: "10px 14px",
                      borderBottom: i < getChangedFields().length - 1 ? "1px solid #f0f0f0" : "none",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{f.label}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                      <span style={{
                        color: "#9ca3af",
                        textDecoration: "line-through",
                        maxWidth: 180,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                        {originalValues[f.key] || "(empty)"}
                      </span>
                      <span style={{ color: "#d1d5db" }}>&rarr;</span>
                      <span style={{
                        color: "#10b981",
                        fontWeight: 500,
                        maxWidth: 180,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                        {formValues[f.key] || "(empty)"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal footer */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                padding: "14px 20px",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: "8px 20px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: "8px 24px",
                  borderRadius: 8,
                  border: "none",
                  background: "linear-gradient(90deg, #1a3f6b, #10b981)",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FaSave size={11} /> Confirm & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TAG_CATEGORIES = ["intent", "lifecycle", "persona", "admin", "other"];

function slugFromLabel(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function ContactTagsSettingsSection() {
  const [defs, setDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Create form
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newCategory, setNewCategory] = useState("persona");
  const [newColor, setNewColor] = useState("#6b7280");
  const [createError, setCreateError] = useState("");
  const [creatingBusy, setCreatingBusy] = useState(false);

  // Per-row edit/delete state
  const [editingSlug, setEditingSlug] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editCategory, setEditCategory] = useState("persona");
  const [editColor, setEditColor] = useState("#6b7280");
  const [editError, setEditError] = useState("");
  const [busySlug, setBusySlug] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      // Bypass the picker's module-level cache so we always see the latest.
      clearTagDefinitionsCache();
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/contact-tags/list-definitions`,
        {},
      );
      setDefs(res?.data?.tags || []);
    } catch (e) {
      setLoadError("Failed to load tags");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const systemTags = defs.filter((d) => d.is_system);
  const customTags = defs.filter((d) => !d.is_system);

  const groupByCategory = (rows) => {
    const map = new Map();
    for (const r of rows) {
      const c = r.category || "other";
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(r);
    }
    const order = [...TAG_CATEGORIES, ...[...map.keys()].filter((c) => !TAG_CATEGORIES.includes(c))];
    return order.filter((c) => map.has(c)).map((c) => [c, map.get(c)]);
  };

  const submitCreate = async () => {
    const label = newLabel.trim();
    const slug = slugFromLabel(label);
    if (!label || !slug) { setCreateError("Label is required"); return; }
    if (!/^#[0-9a-fA-F]{6}$/.test(newColor)) { setCreateError("Pick a hex colour"); return; }
    if (defs.some((d) => d.slug === slug)) { setCreateError("A tag with that name already exists"); return; }
    setCreatingBusy(true);
    setCreateError("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/contact-tags/create-custom`,
        { slug, label, color: newColor, category: newCategory },
      );
      const tag = res?.data?.tag;
      if (!tag) throw new Error("No tag returned");
      clearTagDefinitionsCache();
      setDefs((prev) => [...prev, tag]);
      setCreating(false);
      setNewLabel("");
      setNewColor("#6b7280");
      setNewCategory("persona");
    } catch (e) {
      setCreateError(e?.response?.data?.message || "Failed to create tag");
    } finally {
      setCreatingBusy(false);
    }
  };

  const beginEdit = (tag) => {
    setEditingSlug(tag.slug);
    setEditLabel(tag.label || "");
    setEditCategory(tag.category || "persona");
    setEditColor(tag.color || "#6b7280");
    setEditError("");
  };

  const cancelEdit = () => {
    setEditingSlug(null);
    setEditError("");
  };

  const submitEdit = async (tag) => {
    const label = editLabel.trim();
    if (!label) { setEditError("Label is required"); return; }
    if (!/^#[0-9a-fA-F]{6}$/.test(editColor)) { setEditError("Pick a hex colour"); return; }
    setBusySlug(tag.slug);
    setEditError("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/contact-tags/update-custom`,
        { slug: tag.slug, label, color: editColor, category: editCategory },
      );
      const updated = res?.data?.tag;
      if (!updated) throw new Error("No tag returned");
      clearTagDefinitionsCache();
      setDefs((prev) => prev.map((d) => (d.slug === tag.slug ? updated : d)));
      setEditingSlug(null);
    } catch (e) {
      setEditError(e?.response?.data?.message || "Failed to update tag");
    } finally {
      setBusySlug(null);
    }
  };

  const submitDelete = async (tag) => {
    const ok = window.confirm(
      `Delete tag "${tag.label}"? This removes it from every contact it's been applied to.`,
    );
    if (!ok) return;
    setBusySlug(tag.slug);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/contact-tags/delete-custom`,
        { slug: tag.slug },
      );
      clearTagDefinitionsCache();
      setDefs((prev) => prev.filter((d) => d.slug !== tag.slug));
      if (editingSlug === tag.slug) setEditingSlug(null);
    } catch (e) {
      window.alert(e?.response?.data?.message || "Failed to delete tag");
    } finally {
      setBusySlug(null);
    }
  };

  const sectionLabelStyle = {
    fontSize: 11, fontWeight: 700, color: "#6b7280",
    textTransform: "uppercase", letterSpacing: 0.5,
    margin: "14px 0 6px",
  };

  const categoryLabelStyle = {
    fontSize: 10, fontWeight: 700, color: "#9ca3af",
    textTransform: "uppercase", letterSpacing: 0.5,
    padding: "4px 0",
  };

  return (
    <SettingsSection title="Contact Tags">
      <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 12px", lineHeight: 1.5 }}>
        Tags help organise your contacts and power AI segmentation. System tags are
        shared across all agencies and can't be edited. Custom tags are private to
        your agency — create, rename, recolour, or delete them at any time.
      </p>

      {loadError && (
        <div style={{ fontSize: 12, color: "#dc2626", marginBottom: 10 }}>{loadError}</div>
      )}

      {/* Custom tags */}
      <div style={sectionLabelStyle}>Your custom tags</div>
      {loading ? (
        <div style={{ fontSize: 12, color: "#9ca3af", padding: "8px 0" }}>Loading tags…</div>
      ) : customTags.length === 0 ? (
        <div style={{ fontSize: 12, color: "#9ca3af", padding: "8px 0" }}>
          No custom tags yet. Use the form below to add one.
        </div>
      ) : (
        groupByCategory(customTags).map(([cat, items]) => (
          <div key={cat} style={{ marginBottom: 6 }}>
            <div style={categoryLabelStyle}>{cat}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {items.map((tag) => {
                const isEditing = editingSlug === tag.slug;
                const isBusy = busySlug === tag.slug;
                return (
                  <div
                    key={tag.id}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "6px 8px", borderRadius: 8,
                      border: "1px solid #f1f5f9", background: "#fafafa",
                      flexWrap: "wrap",
                    }}
                  >
                    {isEditing ? (
                      <>
                        <input
                          type="color"
                          value={editColor}
                          onChange={(e) => setEditColor(e.target.value)}
                          title="Chip colour"
                          style={{
                            width: 30, height: 26, padding: 0,
                            border: "1px solid #e5e7eb", borderRadius: 6,
                            background: "#fff", cursor: "pointer",
                          }}
                        />
                        <input
                          value={editLabel}
                          onChange={(e) => { setEditLabel(e.target.value); setEditError(""); }}
                          style={{
                            flex: 1, minWidth: 140,
                            border: "1px solid #d1d5db", borderRadius: 6,
                            padding: "4px 8px", fontSize: 13, outline: "none",
                          }}
                        />
                        <select
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                          style={{
                            fontSize: 12, padding: "4px 6px",
                            border: "1px solid #d1d5db", borderRadius: 6, background: "#fff",
                          }}
                        >
                          {TAG_CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        <ContactTagChip tag={{ label: editLabel || tag.label, color: editColor }} />
                        <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
                          <button
                            onClick={() => submitEdit(tag)}
                            disabled={isBusy}
                            title="Save"
                            style={{
                              background: "#10b981", color: "#fff", border: "none",
                              borderRadius: 6, padding: "4px 8px", fontSize: 12,
                              cursor: isBusy ? "wait" : "pointer",
                              display: "inline-flex", alignItems: "center", gap: 4,
                            }}
                          >
                            {isBusy ? <InlineSpinner size={10} /> : <FaCheck size={10} />} Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={isBusy}
                            title="Cancel"
                            style={{
                              background: "#fff", color: "#374151",
                              border: "1px solid #d1d5db",
                              borderRadius: 6, padding: "4px 8px", fontSize: 12,
                              cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: 4,
                            }}
                          >
                            <FaTimes size={10} /> Cancel
                          </button>
                        </div>
                        {editError && (
                          <div style={{ fontSize: 11, color: "#dc2626", flexBasis: "100%" }}>
                            {editError}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <ContactTagChip tag={tag} />
                        <span style={{ fontSize: 11, color: "#9ca3af" }}>{tag.slug}</span>
                        <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
                          <button
                            onClick={() => beginEdit(tag)}
                            disabled={isBusy}
                            title="Edit"
                            style={{
                              background: "#fff", color: "#374151",
                              border: "1px solid #d1d5db",
                              borderRadius: 6, padding: "4px 8px", fontSize: 12,
                              cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: 4,
                            }}
                          >
                            <FaPen size={10} /> Edit
                          </button>
                          <button
                            onClick={() => submitDelete(tag)}
                            disabled={isBusy}
                            title="Delete"
                            style={{
                              background: "#fff", color: "#dc2626",
                              border: "1px solid #fecaca",
                              borderRadius: 6, padding: "4px 8px", fontSize: 12,
                              cursor: isBusy ? "wait" : "pointer",
                              display: "inline-flex", alignItems: "center", gap: 4,
                            }}
                          >
                            {isBusy ? <InlineSpinner size={10} color="#dc2626" /> : <FaTrash size={10} />} Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Create form */}
      <div style={{ marginTop: 14 }}>
        {creating ? (
          <div
            style={{
              border: "1px solid #e5e7eb", borderRadius: 8,
              padding: 12, background: "#fff",
              display: "flex", flexDirection: "column", gap: 8,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>New custom tag</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="color"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                title="Chip colour"
                style={{
                  width: 32, height: 30, padding: 0,
                  border: "1px solid #e5e7eb", borderRadius: 6,
                  background: "#fff", cursor: "pointer",
                }}
              />
              <input
                autoFocus
                value={newLabel}
                onChange={(e) => { setNewLabel(e.target.value); setCreateError(""); }}
                placeholder="Tag label (e.g. Beach houses)"
                style={{
                  flex: 1, minWidth: 180,
                  border: "1px solid #d1d5db", borderRadius: 6,
                  padding: "6px 10px", fontSize: 13, outline: "none",
                }}
              />
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                style={{
                  fontSize: 12, padding: "6px 8px",
                  border: "1px solid #d1d5db", borderRadius: 6, background: "#fff",
                }}
              >
                {TAG_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            {newLabel && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#6b7280" }}>
                Preview:
                <ContactTagChip tag={{ label: newLabel, color: newColor }} />
                <span style={{ color: "#9ca3af" }}>slug: {slugFromLabel(newLabel) || "—"}</span>
              </div>
            )}
            {createError && (
              <div style={{ fontSize: 12, color: "#dc2626" }}>{createError}</div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                onClick={() => { setCreating(false); setCreateError(""); setNewLabel(""); }}
                style={{
                  background: "#fff", color: "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitCreate}
                disabled={creatingBusy || !newLabel.trim()}
                style={{
                  background: creatingBusy ? "#9ca3af" : "linear-gradient(90deg, #1a3f6b, #10b981)",
                  color: "#fff", border: "none",
                  borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600,
                  cursor: creatingBusy ? "wait" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                {creatingBusy ? <><InlineSpinner size={11} /> Creating…</> : <><FaPlus size={10} /> Create tag</>}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setCreating(true)}
            style={{
              background: "transparent", color: "#1a3f6b",
              border: "1px dashed #1a3f6b",
              borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <FaPlus size={11} /> Add custom tag
          </button>
        )}
      </div>

      {/* System tags (read-only) */}
      <div style={sectionLabelStyle}>System tags (read-only)</div>
      {loading ? (
        <div style={{ fontSize: 12, color: "#9ca3af", padding: "8px 0" }}>Loading tags…</div>
      ) : systemTags.length === 0 ? (
        <div style={{ fontSize: 12, color: "#9ca3af", padding: "8px 0" }}>No system tags.</div>
      ) : (
        groupByCategory(systemTags).map(([cat, items]) => (
          <div key={cat} style={{ marginBottom: 6 }}>
            <div style={categoryLabelStyle}>{cat}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {items.map((tag) => (
                <ContactTagChip key={tag.id} tag={tag} />
              ))}
            </div>
          </div>
        ))
      )}
    </SettingsSection>
  );
}

// Isolated subcomponent so typing in the invite fields doesn't trigger a
// full re-render of the (very large) parent Settings component.
function ReferralSettingsSection() {
  const [referralCode, setReferralCode] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMobile, setInviteMobile] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSms, setPreviewSms] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [copyMsg, setCopyMsg] = useState("");
  const [previewChannel, setPreviewChannel] = useState("email"); // email | sms
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewTimer = useRef(null);
  const previewLastKey = useRef("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    handleTokenAndPostRequest(`${window.api_host}/referral/get-my-code`)
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.status === "success") {
          setReferralCode(res.data.referral_code || "");
          setShareUrl(res.data.share_url || "");
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!referralCode || !previewOpen) return;
    const key = `${inviteName}|${inviteEmail}`;
    if (key === previewLastKey.current && previewHtml) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      previewLastKey.current = key;
      setPreviewLoading(true);
      handleTokenAndPostRequest(
        `${window.api_host}/referral/preview`,
        { to_email: inviteEmail, to_name: inviteName },
        { "Content-Type": "application/json" }
      )
        .then((res) => {
          if (res?.data?.status === "success") {
            setPreviewHtml(res.data.html || "");
            setPreviewSms(res.data.sms_body || "");
          }
        })
        .catch(() => {})
        .finally(() => setPreviewLoading(false));
    }, 600);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
  }, [referralCode, previewOpen, inviteEmail, inviteName, previewHtml]);

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyMsg("Link copied to clipboard.");
      setTimeout(() => setCopyMsg(""), 2500);
    } catch {
      setCopyMsg("Could not copy — select and copy manually.");
    }
  };

  const handleSend = async () => {
    const to = (inviteEmail || "").trim();
    const toMobile = (inviteMobile || "").trim();
    const emailValid = to && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to);
    const mobileValid = toMobile && /^(\+?61|0)4\d{2}\s?\d{3}\s?\d{3}$/.test(toMobile.replace(/\s/g, ""));
    if (!to && !toMobile) { setSendMsg("Enter an email, a mobile number, or both."); return; }
    if (to && !emailValid) { setSendMsg("Enter a valid email address."); return; }
    if (toMobile && !mobileValid) { setSendMsg("Enter a valid Australian mobile (e.g. 0421 346 175)."); return; }

    setSending(true);
    setSendMsg("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/referral/send-invite`,
        { to_email: to, to_mobile: toMobile, to_name: inviteName },
        { "Content-Type": "application/json" }
      );
      const status = res?.data?.status;
      if (status === "success" || status === "partial") {
        const channels = res?.data?.channels || [];
        const parts = [];
        if (channels.includes("email") && to) parts.push(`email to ${to}`);
        if (channels.includes("sms") && toMobile) parts.push(`SMS to ${toMobile}`);
        const summary = parts.length ? parts.join(" and ") : "invite";
        setSendMsg(status === "partial"
          ? (res.data.message || `Sent ${summary}, with a partial failure.`)
          : `Invite sent (${summary}).`);
        if (status === "success") {
          setInviteEmail(""); setInviteMobile(""); setInviteName("");
        }
      } else {
        setSendMsg(res?.data?.message || "Failed to send invite.");
      }
    } catch (err) {
      setSendMsg(err?.response?.data?.message || "Failed to send invite. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <SettingsSection title="Refer another agent — earn $100 SMS credit / month">
      <p style={{ fontSize: 13, color: "#374151", margin: "0 0 16px", lineHeight: 1.55 }}>
        Share Vendorleads with another agent using your personal referral link.
        For every agent who signs up and stays subscribed, you earn{' '}
        <strong>$100 of SMS credit every month</strong> for up to 12 months
        (that's $1,200 of SMS credit). The agent you refer gets{' '}
        <strong>$100 of credit on their first month</strong> as a thank you for trying us.
      </p>

      <div style={{ marginBottom: 18 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
          Your referral link
        </label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            readOnly
            value={loading ? "Loading…" : (shareUrl || "")}
            onFocus={(e) => e.target.select()}
            style={{
              flex: 1, minWidth: 220, padding: "8px 12px", borderRadius: 8,
              border: "1px solid #d1d5db", fontSize: 13, background: "#f9fafb",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          />
          <button
            onClick={handleCopyLink}
            disabled={!shareUrl}
            style={{
              padding: "8px 16px", borderRadius: 8, border: "none",
              background: shareUrl ? "linear-gradient(90deg, #1a3f6b, #10b981)" : "#d1d5db",
              color: "#fff", fontWeight: 600, fontSize: 13,
              cursor: shareUrl ? "pointer" : "default",
              display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
            }}
          >
            <FaExternalLinkAlt size={11} /> Copy link
          </button>
        </div>
        {referralCode && (
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
            Referral code: <code style={{ background: "#f3f4f6", padding: "1px 6px", borderRadius: 4 }}>{referralCode}</code>.
            Anyone who lands on vendorleads.com.au with this link will see the $100 offer in the bottom banner.
          </div>
        )}
        {copyMsg && (
          <div style={{ fontSize: 12, color: "#10b981", marginTop: 6, fontWeight: 600 }}>
            {copyMsg}
          </div>
        )}
      </div>

      <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16, marginTop: 6 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
          Or send an invite directly
        </label>
        <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 10px", lineHeight: 1.5 }}>
          Provide an email, a mobile number, or both — we'll send the agent the offer below
          from Vendorleads with your name on the invite and a tracked link back to your referral code.
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
          <input
            type="text"
            placeholder="Their name (optional)"
            value={inviteName}
            onChange={(e) => { setInviteName(e.target.value); setSendMsg(""); }}
            style={{ flex: 1, minWidth: 140, padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}
          />
          <input
            type="email"
            placeholder="agent@agency.com.au"
            value={inviteEmail}
            onChange={(e) => { setInviteEmail(e.target.value); setSendMsg(""); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !sending) handleSend(); }}
            style={{ flex: 1.4, minWidth: 200, padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}
          />
          <input
            type="tel"
            placeholder="0421 346 175"
            value={inviteMobile}
            onChange={(e) => { setInviteMobile(e.target.value); setSendMsg(""); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !sending) handleSend(); }}
            style={{ flex: 1, minWidth: 160, padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}
          />
          <button
            onClick={handleSend}
            disabled={sending || (!inviteEmail && !inviteMobile)}
            style={{
              padding: "8px 20px", borderRadius: 8, border: "none",
              background: (inviteEmail || inviteMobile) ? "linear-gradient(90deg, #1a3f6b, #10b981)" : "#d1d5db",
              color: "#fff", fontWeight: 600, fontSize: 14,
              cursor: (inviteEmail || inviteMobile) && !sending ? "pointer" : "default",
              display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
            }}
          >
            {sending ? <><InlineSpinner size={13} color="#fff" /> Sending…</> : <><FaEnvelopeOpenText size={12} /> Send invite</>}
          </button>
        </div>
        {sendMsg && (
          <div style={{
            fontSize: 13, fontWeight: 500, marginTop: 6,
            color: sendMsg.startsWith("Invite sent") ? "#10b981" : "#e53935",
          }}>
            {sendMsg}
          </div>
        )}

        {/* Preview opens in a modal — keeps the section short and the
            preview fetch only runs while the modal is open. */}
        <div style={{ marginTop: 14 }}>
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            style={{
              background: "transparent", border: "none", padding: 0, cursor: "pointer",
              color: "#0a9fa6", fontSize: 13, fontWeight: 600,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            Preview what the agent will receive →
          </button>
        </div>
      </div>

      {previewOpen && (
        <ReferralPreviewModal
          onClose={() => setPreviewOpen(false)}
          channel={previewChannel}
          setChannel={setPreviewChannel}
          html={previewHtml}
          sms={previewSms}
          loading={previewLoading || loading}
        />
      )}
    </SettingsSection>
  );
}

function ReferralPreviewModal({ onClose, channel, setChannel, html, sms, loading }) {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // On desktop the left nav is 260px wide and stays visible — offset the
  // backdrop so the modal centers within the main content pane rather than
  // overlapping the sidebar. On mobile the sidebar is a slide-over, so the
  // backdrop covers the full viewport as usual.
  const sidebarOffset = isDesktop ? 260 : 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", top: 0, bottom: 0, right: 0, left: sidebarOffset,
        zIndex: 2000,
        background: "rgba(15,23,42,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14,
          width: "100%", maxWidth: 720, maxHeight: "90vh",
          display: "flex", flexDirection: "column", overflow: "hidden",
          boxShadow: "0 20px 60px rgba(15,23,42,0.35)",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px", borderBottom: "1px solid #f0f0f0",
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>
            Referral message preview
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: "#64748b", padding: 4, lineHeight: 0,
            }}
          >
            <FaTimes size={16} />
          </button>
        </div>

        <div style={{ padding: "12px 18px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 8, padding: 3 }}>
            {[
              { key: "email", label: "Email" },
              { key: "sms", label: "SMS" },
            ].map((opt) => {
              const active = channel === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setChannel(opt.key)}
                  style={{
                    border: "none",
                    background: active ? "#fff" : "transparent",
                    color: active ? "#0f172a" : "#64748b",
                    fontWeight: 700, fontSize: 12,
                    padding: "6px 14px", borderRadius: 6, cursor: "pointer",
                    boxShadow: active ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {loading && (
            <span style={{ fontSize: 12, color: "#94a3b8" }}>updating…</span>
          )}
        </div>

        <div style={{ flex: 1, overflow: "auto", background: channel === "email" ? "#f4f4f4" : "#f8fafc" }}>
          {channel === "email" ? (
            html ? (
              <iframe
                title="Referral email preview"
                srcDoc={html}
                sandbox=""
                style={{ width: "100%", height: 640, border: 0, background: "#f4f4f4", display: "block" }}
              />
            ) : (
              <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                {loading ? "Loading preview…" : "Preview will appear once your referral code is loaded."}
              </div>
            )
          ) : (
            <div style={{ padding: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
                From Vendorleads
              </div>
              <div style={{
                background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
                padding: "12px 14px", fontSize: 14, color: "#0f172a", lineHeight: 1.5,
                whiteSpace: "pre-wrap", wordBreak: "break-word",
                maxWidth: 380,
              }}>
                {sms || (loading ? "Loading preview…" : "Preview will appear once your referral code is loaded.")}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsSection({ title, children }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #e5e7eb",
        marginBottom: 24,
        overflow: "visible",
      }}
    >
      <div
        style={{
          padding: "16px 20px 12px",
          borderBottom: "1px solid #f0f0f0",
          fontWeight: 600,
          fontSize: 15,
          color: "#1a1a1a",
        }}
      >
        {title}
      </div>
      <div style={{ padding: "8px 20px 16px" }}>{children}</div>
    </div>
  );
}

function SettingsField({ field, value, onChange, isChanged, isLocked = false }) {
  const [showHint, setShowHint] = useState(false);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
        <label
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#374151",
          }}
        >
          {field.label}
        </label>
        {isChanged && (
          <span
            style={{
              fontSize: 10,
              background: "#fef3c7",
              color: "#92400e",
              padding: "1px 6px",
              borderRadius: 4,
              fontWeight: 600,
            }}
          >
            Modified
          </span>
        )}
        <FaInfoCircle
          size={12}
          style={{ color: "#9ca3af", cursor: "pointer", marginLeft: 2 }}
          onClick={() => setShowHint((v) => !v)}
        />
      </div>
      {showHint && (
        <div
          style={{
            fontSize: 12,
            color: "#6b7280",
            background: "#f9fafb",
            padding: "6px 10px",
            borderRadius: 6,
            marginBottom: 6,
            lineHeight: 1.5,
          }}
        >
          {field.hint}
        </div>
      )}
      {isLocked && (
        <div
          style={{
            fontSize: 12,
            color: "#92400e",
            background: "#fffbeb",
            padding: "6px 10px",
            borderRadius: 6,
            marginBottom: 6,
            lineHeight: 1.5,
            border: "1px solid #fde68a",
          }}
        >
          This field is managed by the platform and cannot be edited here.
        </div>
      )}
      {field.type === "textarea" ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          disabled={isLocked}
          rows={3}
          style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: isChanged ? "1.5px solid #f59e0b" : "1px solid #d1d5db",
            fontSize: 14,
            resize: "vertical",
            boxSizing: "border-box",
            fontFamily: "inherit",
            background: isChanged ? "#fffbeb" : "#fff",
            opacity: isLocked ? 0.75 : 1,
          }}
        />
      ) : field.type === "color" ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            type="color"
            value={value || "#000000"}
            onChange={(e) => onChange(field.key, e.target.value)}
            disabled={isLocked}
            style={{
              width: 40,
              height: 34,
              border: "1px solid #d1d5db",
              borderRadius: 6,
              padding: 2,
              cursor: isLocked ? "default" : "pointer",
              background: "#fff",
              opacity: isLocked ? 0.75 : 1,
            }}
          />
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(field.key, e.target.value)}
            disabled={isLocked}
            placeholder="#000000"
            style={{
              flex: 1,
              padding: "8px 12px",
              borderRadius: 8,
              border: isChanged ? "1.5px solid #f59e0b" : "1px solid #d1d5db",
              fontSize: 14,
              boxSizing: "border-box",
              background: isChanged ? "#fffbeb" : "#fff",
              opacity: isLocked ? 0.75 : 1,
            }}
          />
        </div>
      ) : (
        <input
          type={field.type || "text"}
          value={value || ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          disabled={isLocked}
          style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: isChanged ? "1.5px solid #f59e0b" : "1px solid #d1d5db",
            fontSize: 14,
            boxSizing: "border-box",
            background: isChanged ? "#fffbeb" : "#fff",
            opacity: isLocked ? 0.75 : 1,
          }}
        />
      )}
    </div>
  );
}

function ImageField({ field, value, uploading, onUpload }) {
  const [showHint, setShowHint] = useState(false);
  const inputRef = useRef(null);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
          {field.label}
        </label>
        <FaInfoCircle
          size={12}
          style={{ color: "#9ca3af", cursor: "pointer", marginLeft: 2 }}
          onClick={() => setShowHint((v) => !v)}
        />
      </div>
      {showHint && (
        <div
          style={{
            fontSize: 12,
            color: "#6b7280",
            background: "#f9fafb",
            padding: "6px 10px",
            borderRadius: 6,
            marginBottom: 6,
            lineHeight: 1.5,
          }}
        >
          {field.hint}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        {value ? (
          <img
            src={value}
            alt={field.label}
            style={{
              width: field.key === "agent_profile_image" ? 64 : 120,
              height: field.key === "agent_profile_image" ? 64 : 48,
              objectFit: field.key === "agent_profile_image" ? "cover" : "contain",
              borderRadius: field.key === "agent_profile_image" ? "50%" : 8,
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
            }}
          />
        ) : (
          <div
            style={{
              width: field.key === "agent_profile_image" ? 64 : 120,
              height: field.key === "agent_profile_image" ? 64 : 48,
              borderRadius: field.key === "agent_profile_image" ? "50%" : 8,
              border: "2px dashed #d1d5db",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f9fafb",
              color: "#aaa",
              fontSize: 11,
            }}
          >
            No image
          </div>
        )}
        <div>
          <input
            type="file"
            accept="image/png,image/jpeg,image/gif"
            ref={inputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                onUpload(e.target.files[0]);
              }
            }}
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              background: "#fff",
              color: "#333",
              fontWeight: 600,
              fontSize: 13,
              cursor: uploading ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <FaUpload size={11} /> {uploading ? <><InlineSpinner size={12} color="#333" /> Uploading...</> : "Upload"}
          </button>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
            PNG, JPG or GIF
          </div>
        </div>
      </div>
    </div>
  );
}

function FontField({ field, value, uploading, onUpload }) {
  const [showHint, setShowHint] = useState(false);
  const [fontFamily, setFontFamily] = useState("");
  const [fontStatus, setFontStatus] = useState("idle"); // idle | loading | ready | error
  const [justUploaded, setJustUploaded] = useState(false);
  const prevValueRef = useRef(value);
  const inputRef = useRef(null);

  const fileName = (() => {
    if (!value) return "";
    try {
      const path = new URL(value).pathname;
      return decodeURIComponent(path.split("/").pop() || "");
    } catch {
      return value.split("/").pop() || "";
    }
  })();

  // Load the font into the document so we can preview it.
  useEffect(() => {
    if (!value || typeof window === "undefined" || !window.FontFace) {
      setFontStatus("idle");
      setFontFamily("");
      return;
    }
    const family = `agent-font-${Math.abs(
      Array.from(value).reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0)
    )}`;
    setFontStatus("loading");
    setFontFamily(family);
    const face = new window.FontFace(family, `url(${value})`);
    let cancelled = false;
    face
      .load()
      .then((loaded) => {
        if (cancelled) return;
        document.fonts.add(loaded);
        setFontStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setFontStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [value]);

  // Flash a success banner when value changes (i.e. a new upload finished).
  useEffect(() => {
    if (value && prevValueRef.current !== value && prevValueRef.current !== undefined) {
      setJustUploaded(true);
      const t = setTimeout(() => setJustUploaded(false), 4000);
      prevValueRef.current = value;
      return () => clearTimeout(t);
    }
    prevValueRef.current = value;
  }, [value]);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
          {field.label}
        </label>
        <FaInfoCircle
          size={12}
          style={{ color: "#9ca3af", cursor: "pointer", marginLeft: 2 }}
          onClick={() => setShowHint((v) => !v)}
        />
      </div>
      {showHint && (
        <div
          style={{
            fontSize: 12,
            color: "#6b7280",
            background: "#f9fafb",
            padding: "6px 10px",
            borderRadius: 6,
            marginBottom: 6,
            lineHeight: 1.5,
          }}
        >
          {field.hint}
        </div>
      )}
      {justUploaded && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#065f46",
            background: "#d1fae5",
            border: "1px solid #6ee7b7",
            padding: "6px 10px",
            borderRadius: 6,
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <FaCheck size={11} /> Font uploaded successfully
        </div>
      )}
      <div style={{ display: "flex", alignItems: "stretch", gap: 14 }}>
        <div
          style={{
            flex: 1,
            minWidth: 240,
            minHeight: 84,
            padding: "10px 14px",
            borderRadius: 8,
            border: value ? "1px solid #e5e7eb" : "2px dashed #d1d5db",
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 4,
          }}
        >
          {value ? (
            <>
              <div
                style={{
                  fontFamily: fontStatus === "ready" && fontFamily ? `"${fontFamily}", system-ui, sans-serif` : "system-ui, sans-serif",
                  fontSize: 22,
                  color: "#111827",
                  lineHeight: 1.2,
                  opacity: fontStatus === "ready" ? 1 : 0.4,
                  transition: "opacity 0.2s",
                }}
              >
                The quick brown fox jumps over the lazy dog
              </div>
              <div
                style={{
                  fontFamily: fontStatus === "ready" && fontFamily ? `"${fontFamily}", system-ui, sans-serif` : "system-ui, sans-serif",
                  fontSize: 14,
                  color: "#4b5563",
                  opacity: fontStatus === "ready" ? 1 : 0.4,
                  transition: "opacity 0.2s",
                }}
              >
                AaBbCc 0123456789 — Your brand typography preview
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                {fontStatus === "loading" && (<><InlineSpinner size={10} color="#9ca3af" /> Loading preview…</>)}
                {fontStatus === "ready" && (<><FaCheck size={10} color="#10b981" /> <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: "#1a3f6b", textDecoration: "none" }}>{fileName || "Font file"}</a></>)}
                {fontStatus === "error" && <span style={{ color: "#b91c1c" }}>Preview unavailable — file saved.</span>}
              </div>
            </>
          ) : (
            <div style={{ color: "#aaa", fontSize: 13, textAlign: "center" }}>
              No font uploaded yet
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <input
            type="file"
            accept=".otf,.ttf,.woff,.woff2,font/otf,font/ttf,font/woff,font/woff2"
            ref={inputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                onUpload(e.target.files[0]);
              }
            }}
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              background: "#fff",
              color: "#333",
              fontWeight: 600,
              fontSize: 13,
              cursor: uploading ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <FaUpload size={11} /> {uploading ? <><InlineSpinner size={12} color="#333" /> Uploading...</> : "Upload"}
          </button>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
            OTF, TTF, WOFF or WOFF2
          </div>
        </div>
      </div>
    </div>
  );
}

function TargetSuburbsField({
  targetSuburbs, suburbQuery, setSuburbQuery, suburbResults, suburbSearching,
  suburbDropdownOpen, suburbDropdownRef, suburbInputRef, searchSuburbs,
  setSuburbDropdownOpen, handleSelectSuburb, handleRemoveSuburb,
  handleSaveSuburbs, savingSuburbs, suburbIdsChanged, suburbSaveMsg,
}) {
  const [showHint, setShowHint] = useState(false);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Your Target Suburbs</label>
        <FaInfoCircle
          size={12}
          style={{ color: "#9ca3af", cursor: "pointer", marginLeft: 2 }}
          onClick={() => setShowHint(v => !v)}
        />
      </div>
      {showHint && (
        <div style={{
          fontSize: 12, color: "#6b7280", background: "#f9fafb",
          padding: "6px 10px", borderRadius: 6, marginBottom: 6, lineHeight: 1.5,
        }}>
          Select the suburbs you operate in. These are used to tailor your market update emails and content to the areas most relevant to your business.
        </div>
      )}

      {/* Selected suburb pills */}
      {targetSuburbs.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {targetSuburbs.map((s, idx) => (
            <span
              key={s.value}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: "#e0f2fe", color: "#0369a1", fontSize: 13, fontWeight: 500,
                padding: "4px 10px", borderRadius: 16,
              }}
            >
              <FaMapMarkerAlt size={10} />
              {s.label || s.value}
              <FaTimes
                size={10}
                style={{ cursor: "pointer", marginLeft: 2, color: "#0369a1" }}
                onClick={() => handleRemoveSuburb(idx)}
              />
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div style={{ position: "relative" }} ref={suburbDropdownRef}>
        <input
          ref={suburbInputRef}
          type="text"
          value={suburbQuery}
          onChange={(e) => { setSuburbQuery(e.target.value); searchSuburbs(e.target.value); }}
          onFocus={() => { if (suburbResults.length > 0) setSuburbDropdownOpen(true); }}
          placeholder="Search for a suburb..."
          style={{
            width: "100%", padding: "8px 12px", borderRadius: 8,
            border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box",
          }}
        />
        {suburbSearching && (
          <span style={{
            position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
            display: "inline-flex", alignItems: "center",
          }}>
            <InlineSpinner size={14} color="#9ca3af" />
          </span>
        )}

        {/* Dropdown */}
        {suburbDropdownOpen && suburbResults.length > 0 && (
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
            background: "#fff", border: "1px solid #d1d5db", borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)", maxHeight: 220, overflowY: "auto",
            marginTop: 4,
          }}>
            {suburbResults.map((item, i) => (
              <div
                key={item.value + item.type}
                onClick={() => handleSelectSuburb(item)}
                style={{
                  padding: "8px 12px", cursor: "pointer", fontSize: 14, color: "#1f2937",
                  background: "#fff", borderBottom: i < suburbResults.length - 1 ? "1px solid #f0f0f0" : "none",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"}
                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
              >
                <FaMapMarkerAlt size={10} style={{ color: "#9ca3af", marginRight: 6 }} />
                {item.label || item.value}
                {item.state && <span style={{ color: "#9ca3af", marginLeft: 4, fontSize: 12 }}>{item.state}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save suburbs button */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
        <button
          onClick={handleSaveSuburbs}
          disabled={!suburbIdsChanged || savingSuburbs}
          style={{
            padding: "8px 20px", borderRadius: 8, border: "none",
            background: suburbIdsChanged ? "linear-gradient(90deg, #1a3f6b, #10b981)" : "#d1d5db",
            color: "#fff", fontWeight: 600, fontSize: 14,
            cursor: suburbIdsChanged ? "pointer" : "default",
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          {savingSuburbs ? <><InlineSpinner size={13} color="#fff" /> Saving...</> : <><FaSave size={11} /> Save Suburbs</>}
        </button>
        {suburbSaveMsg && (
          <span style={{
            fontSize: 13, fontWeight: 500,
            color: suburbSaveMsg.includes("success") ? "#10b981" : "#e53935",
          }}>
            {suburbSaveMsg}
          </span>
        )}
      </div>
    </div>
  );
}
